import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';

const ERROR_DISPLAY_MS = 10000;
const ERROR_REENABLE_MS = 1000;

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {number} price
 * @property {number} [compare_at_price]
 * @property {boolean} available
 * @property {string} option1
 * @property {string} [option2]
 * @property {string} [option3]
 * @property {string} [sku]
 * @property {{ src?: string, alt?: string } | null} [featured_image]
 */

/**
 * @typedef {{ opt1: boolean, opt2: boolean, opt3: boolean, isComplementary?: boolean }} OptionMatches
 */

/**
 * @typedef {Object} BundleProductState
 * @property {number} variantId
 * @property {number} productId
 * @property {string} productHandle
 * @property {number} price
 * @property {string} sku
 * @property {boolean} isSelected
 * @property {OptionMatches} optionMatches - per-option match state against PDP
 * @property {Variant[]} variants
 */

/**
 * Bundle offer component: current PDP product + up to 2 additional products
 * from a product-level JSON metafield with auto-matching and dynamic discounts.
 * Supports products with multiple variant options such as Config + Größe.
 * @extends {Component}
 */
export class BuyTheBundleComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number[]} */
  #resetTimeouts = [];
  /** @type {number | undefined} */
  #errorTimeout;
  /** @type {number} */
  #primaryVariantId = 0;
  /** @type {number} */
  #primaryPrice = 0;
  /** @type {string} */
  #primarySku = '';
  /** @type {{ option1: string, option2: string, option3: string }} */
  #pdpOptions = { option1: '', option2: '', option3: '' };
  /** @type {boolean} */
  #pdpIsDefaultTitle = false;
  /** @type {Map<number, BundleProductState>} */
  #bundleProducts = new Map();
  /** @type {Map<number, any>} */
  #bundleFullProducts = new Map();
  /** @type {Map<number, Variant[]>} */
  #bundleVariants = new Map();
  /** @type {Map<number, Map<number, number>>} */
  #bundleEstimatedRetailPrices = new Map();
  /** @type {Map<number, { productTitle: string, variants: Map<number, string> }>} */
  #bundleVariantNames = new Map();
  /** @type {Map<number, Map<number, string>>} */
  #bundleVariantImages = new Map();
  /** @type {{ productTitle: string, variants: Map<number, string> } | null} */
  #primaryVariantNames = null;
  /** @type {Map<number, string>} */
  #primaryVariantImages = new Map();
  /** @type {Variant[]} */
  #primaryVariants = [];
  /** @type {Map<number, number>} */
  #primaryEstimatedRetailPrices = new Map();
  /** @type {Map<number, any>} */
  #bundleMetafieldData = new Map();
  /** @type {any} */
  #primaryMetafieldData = null;
  /** @type {number} */
  #discount2 = 0;
  /** @type {number} */
  #discount3 = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#parseConfig();
    this.#parseBundleFullProducts();
    this.#parseBundleVariants();
    this.#parseBundleMetafields();
    this.#parsePrimaryVariants();
    this.#parsePrimaryMetafields();
    this.#parsePrimaryVariantNames();
    this.#initBundleProducts();

    // Re-verify parsing after DOM is fully parsed
    setTimeout(() => {
      if (!this.#primaryVariants.length) this.#parsePrimaryVariants();
      if (!this.#bundleVariants.size) this.#parseBundleVariants();
      if (!this.#primaryVariantImages.size) this.#parsePrimaryMetafields();
      if (!this.#bundleVariantImages.size) this.#parseBundleMetafields();
    }, 50);

    const primaryProduct = /** @type {HTMLElement | null} */ (this.refs.primaryProduct);
    if (primaryProduct instanceof HTMLElement) {
      this.#primaryVariantId = parseInt(primaryProduct.dataset.variantId || '0', 10);
      this.#primaryPrice = parseInt(primaryProduct.dataset.price || '0', 10);
      this.#primarySku = primaryProduct.dataset.sku || '';
      this.#setBundleVisibility(primaryProduct.dataset.available === 'true');
    }

    this.#initMobileToggle(signal);
    this.#initCheckboxes(signal);
    this.#initCustomDropdowns(signal);
    this.#reorderVariantOptions();
    this.#initQuickViewTriggers(signal);
    this.#listenToPdpVariantChange(signal);

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (/** @type {Event} */ e) => this.#handleAddToCart(e), { signal });
    }

    const primaryInputs = this.#getOptionInputs(0);
    if (primaryInputs.length > 0 && this.#primaryVariants.length > 0) {
      this.#updateOptionAvailability(0, this.#readSelectedOptions(primaryInputs), this.#primaryVariants);
    }

    for (const [i, variants] of this.#bundleVariants) {
      const inputs = this.#getOptionInputs(i);
      if (inputs.length > 0) this.#updateOptionAvailability(i, this.#readSelectedOptions(inputs), variants);
    }

    this.#updateItemCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    for (const id of this.#resetTimeouts) clearTimeout(id);
    this.#resetTimeouts = [];
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  // --- Initialization ---

  #parseConfig() {
    this.#discount2 = Number(this.dataset.discount2 || 0);
    this.#discount3 = Number(this.dataset.discount3 || 0);
    this.#pdpIsDefaultTitle = this.dataset.pdpIsDefaultTitle === 'true';
    this.#pdpOptions = {
      option1: this.dataset.pdpOption1Value || '',
      option2: this.dataset.pdpOption2Value || '',
      option3: this.dataset.pdpOption3Value || '',
    };
  }

  #parseBundleVariants() {
    for (let i = 1; i <= 2; i++) {
      const el = this.querySelector(`script[data-bundle-variants="${i}"]`) || document.querySelector(`script[data-bundle-variants="${i}"]`);
      if (el) {
        try {
          const parsed = JSON.parse(el.textContent || '[]');
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.#bundleVariants.set(i, /** @type {Variant[]} */ (parsed));
          }
        } catch (e) {
          console.error(`[BuyTheBundle] Error parsing script[data-bundle-variants="${i}"]:`, e);
        }
      }

      // Fallback from bundleFullProducts
      if (!this.#bundleVariants.has(i) || !this.#bundleVariants.get(i)?.length) {
        const prod = this.#bundleFullProducts.get(i);
        if (prod?.variants && Array.isArray(prod.variants) && prod.variants.length > 0) {
          this.#bundleVariants.set(i, prod.variants);
        }
      }

      // Fallback from DOM dropdown items
      if (!this.#bundleVariants.has(i) || !this.#bundleVariants.get(i)?.length) {
        const items = this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${i}"] .buy-the-bundle__custom-dropdown-item`);
        if (items.length > 0) {
          /** @type {Variant[]} */
          const domVariants = [];
          for (const item of items) {
            const vId = parseInt(item.getAttribute('data-variant-id') || '0', 10);
            const val = item.getAttribute('data-value') || '';
            const img = item.getAttribute('data-image') || '';
            if (vId) {
              domVariants.push({
                id: vId,
                price: 0,
                available: item.getAttribute('data-available') !== 'false',
                option1: val,
                featured_image: img ? { src: img } : null,
              });
            }
          }
          if (domVariants.length > 0) this.#bundleVariants.set(i, domVariants);
        }
      }
    }
  }

  #parseBundleMetafields() {
    for (let i = 1; i <= 2; i++) {
      const el = this.querySelector(`script[data-bundle-metafields="${i}"]`) || document.querySelector(`script[data-bundle-metafields="${i}"]`);
      if (!el) continue;
      try {
        const raw = JSON.parse(el.textContent || '{}');
        this.#bundleMetafieldData.set(i, raw);
        const estimatedMap = /** @type {Map<number, number>} */ (new Map());
        const namesMap = /** @type {Map<number, string>} */ (new Map());
        const imagesMap = /** @type {Map<number, string>} */ (new Map());
        if (raw.variants) {
          for (const [vid, data] of Object.entries(raw.variants)) {
            const id = parseInt(vid, 10);
            if (!id) continue;
            const d = /** @type {{ estimatedRetail?: number, name?: string, variantTitle?: string, imageUrl?: string }} */ (data);
            if (Number(d.estimatedRetail) > 0) estimatedMap.set(id, Number(d.estimatedRetail));
            const vName = String(d.variantTitle || d.name || '').trim();
            if (vName) namesMap.set(id, vName);
            if (d.imageUrl) imagesMap.set(id, String(d.imageUrl));
          }
        }
        this.#bundleEstimatedRetailPrices.set(i, estimatedMap);
        this.#bundleVariantNames.set(i, { productTitle: raw.productTitle || '', variants: namesMap });
        this.#bundleVariantImages.set(i, imagesMap);
        } catch (e) {
          console.error(`[BuyTheBundle] Error parsing script[data-bundle-metafields="${i}"]:`, e, el.textContent);
        }
      }
    }

  #parseBundleFullProducts() {
    for (let i = 0; i <= 2; i++) {
      const el = this.querySelector(`script[data-bundle-product-data="${i}"], script[data-bundle-full-product="${i}"]`) || document.querySelector(`script[data-bundle-product-data="${i}"], script[data-bundle-full-product="${i}"]`);
      if (!el) continue;
      try {
        const prod = JSON.parse(el.textContent || '{}');
        if (prod && (prod.id || (prod.variants && prod.variants.length > 0))) {
          this.#bundleFullProducts.set(i, prod);
        }
      } catch { /* full product data unavailable */ }
    }
  }

  #parsePrimaryVariants() {
     const el = this.querySelector('script[data-primary-variants]') || document.querySelector('script[data-primary-variants]');
    if (el) {
      try {
        const parsed = JSON.parse(el.textContent || '[]');
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.#primaryVariants = /** @type {Variant[]} */ (parsed);
        }
      } catch (e) {
        console.error('[BuyTheBundle] Error parsing script[data-primary-variants]:', e);
      }
    }

    // Fallback from bundleFullProducts[0]
    if (!this.#primaryVariants.length) {
      const prod0 = this.#bundleFullProducts.get(0);
      if (prod0?.variants && Array.isArray(prod0.variants) && prod0.variants.length > 0) {
        this.#primaryVariants = prod0.variants;
      }
    }

    // Fallback from DOM dropdown items for primary product
    if (!this.#primaryVariants.length) {
      const items = this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="0"] .buy-the-bundle__custom-dropdown-item`);
      if (items.length > 0) {
        /** @type {Variant[]} */
        const domVariants = [];
        for (const item of items) {
          const vId = parseInt(item.getAttribute('data-variant-id') || '0', 10);
          const val = item.getAttribute('data-value') || '';
          const img = item.getAttribute('data-image') || '';
          if (vId) {
            domVariants.push({
              id: vId,
              price: 0,
              available: item.getAttribute('data-available') !== 'false',
              option1: val,
              featured_image: img ? { src: img } : null,
            });
          }
        }
        if (domVariants.length > 0) this.#primaryVariants = domVariants;
      }
    }
  }

  #parsePrimaryMetafields() {
    const el = this.querySelector('script[data-primary-metafields]') || document.querySelector('script[data-primary-metafields]');    if (!el) return;
    try {
      const raw = JSON.parse(el.textContent || '{}');
      this.#primaryMetafieldData = raw;
      const estimatedMap = /** @type {Map<number, number>} */ (new Map());
      const imagesMap = /** @type {Map<number, string>} */ (new Map());
      if (raw.variants) {
        for (const [vid, data] of Object.entries(raw.variants)) {
          const id = parseInt(vid, 10);
          if (!id) continue;
          const d = /** @type {{ estimatedRetail?: number }} */ (data);
          if (Number(d.estimatedRetail) > 0) estimatedMap.set(id, Number(d.estimatedRetail));
          if (d.imageUrl) imagesMap.set(id, String(d.imageUrl));
        }
      }
      this.#primaryEstimatedRetailPrices = estimatedMap;
      this.#primaryVariantImages = imagesMap;
    } catch (e) {
      console.error('[BuyTheBundle] Error parsing script[data-primary-metafields]:', e, el.textContent);
    }
  }

  #parsePrimaryVariantNames() {
    const el = this.querySelector('script[data-primary-variant-names]') || document.querySelector('script[data-primary-variant-names]');
    if (!el) return;
    try {
      const raw = JSON.parse(el.textContent || '{}');
      const map = /** @type {Map<number, string>} */ (new Map());
      if (raw.variants) {
        for (const [vid, name] of Object.entries(raw.variants)) {
          const id = parseInt(vid, 10);
          if (id) map.set(id, String(name || ''));
        }
      }
      this.#primaryVariantNames = { productTitle: raw.productTitle || '', variants: map };
    } catch { /* primary names unavailable */ }
  }

  #initBundleProducts() {
    for (let i = 1; i <= 2; i++) {
      const ref = this.refs[`bundleProduct${i}`];
      const card = /** @type {HTMLElement | null} */ (Array.isArray(ref) ? ref[0] : ref);
      if (!card || !(card instanceof HTMLElement)) continue;

      this.#bundleProducts.set(i, {
        variantId: parseInt(card.dataset.variantId || '0', 10),
        productId: parseInt(card.dataset.productId || '0', 10),
        productHandle: card.dataset.productHandle || '',
        price: parseInt(card.dataset.price || '0', 10),
        sku: card.dataset.sku || '',
        isSelected: true,
        optionMatches: {
          opt1: card.dataset.opt1Matched === 'true',
          opt2: card.dataset.opt2Matched === 'true',
          opt3: card.dataset.opt3Matched === 'true',
        },
        variants: this.#bundleVariants.get(i) || [],
      });
    }
  }

  // --- PDP variant change ---

  /** @param {AbortSignal} signal */
  #listenToPdpVariantChange(signal) {
    const handleVariant = (variant, eventProductId) => {
      if (!variant?.id) return;
      const myProductId = this.dataset.productId;
      if (myProductId && eventProductId && String(myProductId) !== String(eventProductId)) return;

      this.#primaryVariantId = variant.id;
      this.#primaryPrice = variant.price || 0;
      this.#primarySku = variant.sku || '';
      this.#pdpIsDefaultTitle = variant.title === 'Default Title';

      const newOptions = {
        option1: variant.option1 || '',
        option2: variant.option2 || '',
        option3: variant.option3 || '',
      };
      this.#pdpOptions = newOptions;

      this.#syncDropdownsToVariant(0, variant);
      this.#updatePrimaryProductName(variant.id);
      this.#updatePrimaryProductPricing(variant);
      this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.primaryImageWrapper), variant, 0);
      this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.mobilePrimaryImageWrapper), variant, 0);
      this.#updatePrimaryProductDataset(variant);

      const primaryInputs = this.#getOptionInputs(0);
      if (primaryInputs.length > 0 && this.#primaryVariants.length > 0) {
        this.#updateOptionAvailability(0, this.#readSelectedOptions(primaryInputs), this.#primaryVariants);
      }

      this.#rematchAllBundleProducts();

      if (variant.available === false) this.#setBundleVisibility(false);
      this.#updatePrices();
    };

    const resolveVariant = (detail) => {
      if (detail?.resource && !Array.isArray(detail.resource) && detail.resource.id) {
        return detail.resource;
      }
      if (detail?.variant && !Array.isArray(detail.variant) && detail.variant.id) {
        return detail.variant;
      }

      if (!this.#primaryVariants.length) {
        this.#parsePrimaryVariants();
      }

      const variants = this.#primaryVariants.length > 0
        ? this.#primaryVariants
        : (Array.isArray(detail?.resource) ? detail.resource : (Array.isArray(detail?.variant) ? detail.variant : []));

      const html = detail?.data?.html;
      if (html && variants.length > 0) {
        const formVariantId = html.querySelector('product-form input[name="id"], input[name="id"]')?.value;
        if (formVariantId) {
          const found = variants.find((v) => String(v.id) === String(formVariantId));
          if (found) return found;
        }

        const checkedVariantId = html.querySelector('variant-picker input[data-current-checked="true"], variant-picker input:checked')?.dataset.variantId;
        if (checkedVariantId) {
          const found = variants.find((v) => String(v.id) === String(checkedVariantId));
          if (found) return found;
        }
      }

      const variantTitle = detail?.data?.variantTitle;
      if (variantTitle && variants.length > 0) {
        const found = variants.find((v) => v.title === variantTitle || v.option1 === variantTitle);
        if (found) return found;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      if (urlVariantId && variants.length > 0) {
        const found = variants.find((v) => String(v.id) === String(urlVariantId));
        if (found) return found;
      }

      const livePicker = document.querySelector('variant-picker');
      if (livePicker && variants.length > 0) {
        const checkedInputs = Array.from(livePicker.querySelectorAll('input:checked, select'));
        if (checkedInputs.length > 0) {
          const selectedValues = checkedInputs.map((el) => (el instanceof HTMLSelectElement ? el.value : el.value || el.dataset.value));
          const found = variants.find((v) => {
            const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
            return selectedValues.every((val) => vOpts.includes(val));
          });
          if (found) return found;
        }
      }

      return null;
    };

    const handleEvent = (event) => {
      const variant = resolveVariant(event.detail);
      const productId = event.detail?.data?.productId || event.detail?.productId;
      if (variant) {
        handleVariant(variant, productId);
      }
    };

    document.addEventListener(ThemeEvents.variantUpdate, handleEvent, { signal });
    document.addEventListener('variant:update', handleEvent, { signal });
    document.addEventListener(
      'variantSelected:reinitBuybox',
      () => {
        const variant = resolveVariant({});
        if (variant) handleVariant(variant, this.dataset.productId);
      },
      { signal },
    );
    document.addEventListener(
      'variantUpdated:reinitBuybox',
      () => {
        const variant = resolveVariant({});
        if (variant) handleVariant(variant, this.dataset.productId);
      },
      { signal },
    );

    document.addEventListener(
      'change',
      (e) => {
        if (e.target instanceof Element && e.target.closest('variant-picker')) {
          setTimeout(() => {
            const variant = resolveVariant({});
            if (variant) handleVariant(variant, this.dataset.productId);
          }, 0);
        }
      },
      { signal },
    );
  }

  #rematchAllBundleProducts() {
    for (const [idx, bp] of this.#bundleProducts) {
      const variants = this.#bundleVariants.get(idx);
      if (!variants?.length) continue;

      const matches = this.#matchOptionsPerProduct(idx, variants);
      bp.optionMatches = matches;

      // Product is eligible if ANY option matches PDP.
      // Needed for products with 2 options like Config + Größe, and also if option order differs.
      const hasAnyMatchedOption = matches.opt1 || matches.opt2 || matches.opt3;

      if (!this.#pdpIsDefaultTitle && !hasAnyMatchedOption && !matches.isComplementary) {
        bp.isSelected = false;
        const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
        if (card instanceof HTMLElement) card.style.display = 'none';
        continue;
      } else {
        const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
        if (card instanceof HTMLElement) card.style.display = '';
      }

      const bestVariant = this.#findBestVariant(variants, matches);
      if (bestVariant) {
        bp.variantId = bestVariant.id;
        bp.price = bestVariant.price;
        bp.sku = bestVariant.sku || '';
        this.#syncDropdownsToVariant(idx, bestVariant);
        this.#updateBundleProductDisplay(idx, bestVariant);
      }

      this.#toggleDropdownVisibility(idx, matches);

      const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
      if (card instanceof HTMLElement) {
        card.dataset.opt1Matched = String(matches.opt1);
        card.dataset.opt2Matched = String(matches.opt2);
        card.dataset.opt3Matched = String(matches.opt3);
      }

      const inputs = this.#getOptionInputs(idx);
      if (inputs.length > 0) this.#updateOptionAvailability(idx, this.#readSelectedOptions(inputs), variants);
    }

    // Hide entire bundle if no sub-product remains eligible
    let anyEligible = false;
    for (const [, bp] of this.#bundleProducts) {
      if (bp.isSelected) { anyEligible = true; break; }
    }
    this.#setBundleVisibility(anyEligible);
  }

  /**
   * Check bundle product options against PDP selected option values.
   * Works even when PDP option order and bundle option order are different.
   * Example: PDP option1 = Größe, bundle option2 = Größe.
   * @param {number} idx
   * @param {Variant[]} variants
   * @returns {OptionMatches}
   */
  #matchOptionsPerProduct(idx, variants) {
    void idx;

    const pdpValues = [
      this.#pdpOptions.option1,
      this.#pdpOptions.option2,
      this.#pdpOptions.option3,
    ]
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean);

    /** @param {Variant[]} vs @param {1|2|3} pos @returns {string[]} */
    const getOptionValues = (vs, pos) => vs
      .map((v) => (pos === 1 ? v.option1 : pos === 2 ? v.option2 : v.option3)?.toLowerCase())
      .filter(/** @returns {s is string} */ (s) => typeof s === 'string' && s !== '');

    const availableVariants = variants.filter((v) => v.available);

    const allOpt1 = getOptionValues(availableVariants, 1);
    const allOpt2 = getOptionValues(availableVariants, 2);
    const allOpt3 = getOptionValues(availableVariants, 3);

    // If PDP option values share no overlap with any bundle option values at all,
    // the products are complementary (different types) — always eligible, but keep opt flags false
    // so dropdowns are not hidden by #toggleDropdownVisibility.
    const allBundleValues = [...allOpt1, ...allOpt2, ...allOpt3];
    const hasAnyOverlap = allBundleValues.some((bundleValue) =>
      pdpValues.some((pdpValue) => bundleValue.includes(pdpValue) || pdpValue.includes(bundleValue)),
    );
    if (!hasAnyOverlap) return { opt1: false, opt2: false, opt3: false, isComplementary: true };

    /** @param {string[]} optionValues */
    const hasMatch = (optionValues) => {
      if (pdpValues.length === 0) return false;

      return optionValues.some((bundleValue) =>
        pdpValues.some((pdpValue) => bundleValue.includes(pdpValue) || pdpValue.includes(bundleValue)),
      );
    };

    return {
      opt1: this.#pdpIsDefaultTitle ? true : hasMatch(allOpt1),
      opt2: hasMatch(allOpt2),
      opt3: hasMatch(allOpt3),
    };
  }

  /**
   * Find the best available variant given per-option match constraints.
   * Supports products with multiple options like Config + Größe.
   * @param {Variant[]} variants
   * @param {OptionMatches} matches
   * @returns {Variant | undefined}
   */
  #findBestVariant(variants, matches) {
    const o1 = this.#pdpOptions.option1.toLowerCase();
    const o2 = this.#pdpOptions.option2.toLowerCase();
    const o3 = this.#pdpOptions.option3.toLowerCase();

    const pdpValues = [o1, o2, o3].filter(Boolean);
    const skipOpt1 = this.#pdpIsDefaultTitle;

    /**
     * @param {string | undefined} variantValue
     * @param {string} samePositionValue
     */
    const matchesPdpValue = (variantValue, samePositionValue) => {
      const value = String(variantValue || '').toLowerCase();
      if (!value) return false;

      if (samePositionValue && value.includes(samePositionValue)) return true;

      return pdpValues.some((pdpValue) => value.includes(pdpValue) || pdpValue.includes(value));
    };

    const best = variants.find((v) => {
      if (!v.available) return false;

      if (!skipOpt1 && matches.opt1 && !matchesPdpValue(v.option1, o1)) return false;
      if (matches.opt2 && !matchesPdpValue(v.option2, o2)) return false;
      if (matches.opt3 && !matchesPdpValue(v.option3, o3)) return false;

      return true;
    });

    if (best) return best;

    return variants.find((v) => v.available);
  }

  /**
   * @param {number} idx
   * @param {Variant} variant
   */
  #syncDropdownsToVariant(idx, variant) {
    const optionValues = [variant.option1, variant.option2, variant.option3];

    for (let pos = 0; pos < 3; pos++) {
      const value = optionValues[pos];
      if (!value) continue;

      const wrapper =
        this.querySelector(
          `.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"][data-option-position="${pos + 1}"]`,
        ) ||
        this.querySelector(
          `.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"][data-option-index="${pos}"]`,
        );
      if (!wrapper) continue;

      const input = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      if (!(input instanceof HTMLInputElement) || !button || !list) continue;

      input.value = value;
      this.#updateDropdownDisplay(button, list, value);
    }
  }

  /**
   * Toggle visibility per option.
   * For products with 2+ options, keep all dropdowns visible.
   * Example: Größe + Config should both stay visible.
   * @param {number} idx
   * @param {OptionMatches} matches
   */
  #toggleDropdownVisibility(idx, matches) {
    const wrappers = Array.from(
      this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"]`),
    );

    const shouldHideMatchedDropdowns = wrappers.length === 1;

    for (const wrapper of wrappers) {
      const optionDiv = wrapper.closest('.buy-the-bundle__variant-option');
      if (!optionDiv) continue;

      const posStr = /** @type {HTMLElement} */ (wrapper).dataset.optionIndex;
      const isSize = /** @type {HTMLElement} */ (wrapper).dataset.isSize === 'true';
      const pos = parseInt(posStr || '0', 10);

      let matched = false;
      if (pos === 0) matched = matches.opt1;
      else if (pos === 1) matched = matches.opt2;
      else if (pos === 2) matched = matches.opt3;

      optionDiv.classList.toggle(
        'buy-the-bundle__variant-option--hidden',
        shouldHideMatchedDropdowns && matched && !isSize,
      );
    }
  }

  // --- Internal variant change (dropdown selection) ---

  /**
   * @param {HTMLInputElement} input
   * @param {number} [directVariantId]
   */
  #handleVariantChange(input, directVariantId) {
    const idx = parseInt(input.dataset.bundleIndex || '', 10);
    if (Number.isNaN(idx)) return;

    if (idx === 0) {
      this.#handlePrimaryVariantChange(directVariantId);
      return;
    }

    const bp = this.#bundleProducts.get(idx);
    const variants = this.#bundleVariants.get(idx);
    if (!bp || !variants) {
      console.warn(`[BuyTheBundle] Missing bundle product or variants for index ${idx}`);
      return;
    }

    const allInputs = this.#getOptionInputs(idx);
    this.#updateOptionAvailability(idx, this.#readSelectedOptions(allInputs), variants);

    const refreshed = this.#readSelectedOptions(allInputs);
    let match = this.#findVariantBySelectedOptions(variants, refreshed);
    if (!match && directVariantId && allInputs.length <= 1) {
      match = variants.find((v) => v.id === directVariantId);
    }

    if (match) {
      bp.variantId = match.id;
      bp.price = match.price;
      bp.sku = match.sku || '';
      this.#updateBundleProductDisplay(idx, match);
      this.#syncDropdownsToVariant(idx, match);
      this.#updatePrices();
    }
  }

   /**
   * @param {number} [directVariantId]
   */
  #handlePrimaryVariantChange(directVariantId) {
    const variants = this.#primaryVariants;
    if (!variants.length) {
      console.warn('[BuyTheBundle] Missing primary variants');
      return;
    }

    const allInputs = this.#getOptionInputs(0);
    this.#updateOptionAvailability(0, this.#readSelectedOptions(allInputs), variants);

    const refreshed = this.#readSelectedOptions(allInputs);
    let match = this.#findVariantBySelectedOptions(variants, refreshed);
    if (!match && directVariantId && allInputs.length <= 1) {
      match = variants.find((v) => v.id === directVariantId);
    }
    if (!match) return;

    this.#primaryVariantId = match.id;
    this.#primaryPrice = match.price || 0;
    this.#primarySku = match.sku || '';
    this.#pdpIsDefaultTitle = /** @type {any} */ (match).title === 'Default Title';
    this.#pdpOptions = {
      option1: match.option1 || '',
      option2: match.option2 || '',
      option3: match.option3 || '',
    };

    this.#syncDropdownsToVariant(0, match);
    this.#updatePrimaryProductName(match.id);
    this.#updatePrimaryProductPricing(match);
    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.primaryImageWrapper), match, 0);
    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs.mobilePrimaryImageWrapper), match, 0);
    this.#updatePrimaryProductDataset(match);
    this.#updatePrices();
  }

  /**
   * @param {Variant[]} variants
   * @param {Record<string, string>} selectedOptions
   * @returns {Variant | undefined}
   */
  #findVariantBySelectedOptions(variants, selectedOptions) {
    if (!variants || !variants.length) return undefined;

    const normalize = (val) => String(val || '').trim().toLowerCase();
    const optMatch = (val1, val2) => {
      const n1 = normalize(val1);
      const n2 = normalize(val2);
      if (!n1 || !n2) return true;
      if (n1 === n2) return true;
      const num1 = parseFloat(n1.replace(',', '.'));
      const num2 = parseFloat(n2.replace(',', '.'));
      if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;
      return false;
    };

    const opt1 = selectedOptions.option1;
    const opt2 = selectedOptions.option2;
    const opt3 = selectedOptions.option3;

    // 1. Try exact match on available variant
    let match = variants.find((v) => {
      if (!v.available) return false;
      return (
        (!opt1 || optMatch(v.option1, opt1)) &&
        (!opt2 || optMatch(v.option2, opt2)) &&
        (!opt3 || optMatch(v.option3, opt3))
      );
    });
    if (match) return match;

    // 2. Try match on any variant (even if out of stock)
    match = variants.find((v) => {
      return (
        (!opt1 || optMatch(v.option1, opt1)) &&
        (!opt2 || optMatch(v.option2, opt2)) &&
        (!opt3 || optMatch(v.option3, opt3))
      );
    });
    if (match) return match;

    // 3. Fallback to matching option1 only if available
    if (opt1) {
      match = variants.find((v) => v.available && optMatch(v.option1, opt1));
      if (match) return match;
      match = variants.find((v) => optMatch(v.option1, opt1));
      if (match) return match;
    }

    return variants[0];
  }


  /**
   * @param {number} idx
   * @param {Record<string, string>} selectedOptions
   * @param {Variant[]} variants
   */
  #updateOptionAvailability(idx, selectedOptions, variants) {
    const wrappers = this.querySelectorAll(`.buy-the-bundle__custom-dropdown-wrapper[data-bundle-index="${idx}"]`);

    const normalize = (val) => String(val || '').trim().toLowerCase();
    const optMatch = (val1, val2) => {
      const n1 = normalize(val1);
      const n2 = normalize(val2);
      if (!n1 || !n2) return true;
      if (n1 === n2) return true;
      const num1 = parseFloat(n1.replace(',', '.'));
      const num2 = parseFloat(n2.replace(',', '.'));
      if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;
      return false;
    };

    for (const wrapper of wrappers) {
      const hiddenInput = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      if (!hiddenInput || !button || !list) continue;

      const pos = /** @type {HTMLInputElement} */ (hiddenInput).dataset.optionPosition;
      if (!pos) continue;

      const items = list.querySelectorAll('.buy-the-bundle__custom-dropdown-item');
      let hasAvailableSelected = false;
      let firstAvailableValue = /** @type {string | null} */ (null);
      const currentValue = /** @type {HTMLInputElement} */ (hiddenInput).value;

      for (const item of items) {
        const el = /** @type {HTMLElement} */ (item);
        const optValue = el.getAttribute('data-value') || '';
        const textEl = el.querySelector('.buy-the-bundle__custom-dropdown-item-text');
        if (!textEl) continue;

        if (!el.dataset.originalText) {
          el.dataset.originalText = (textEl.textContent || '')
            .replace(/\s*-\s*(Out of stock|Ausverkauft)/gi, '')
            .trim();
        }

        const test = { ...selectedOptions, [`option${pos}`]: optValue };
        const exists = variants.some((v) => {
          return (
            (!test.option1 || optMatch(v.option1, test.option1)) &&
            (!test.option2 || optMatch(v.option2, test.option2)) &&
            (!test.option3 || optMatch(v.option3, test.option3))
          );
        });

        const available = exists && variants.some((v) => {
          if (!v.available) return false;
          return (
            (!test.option1 || optMatch(v.option1, test.option1)) &&
            (!test.option2 || optMatch(v.option2, test.option2)) &&
            (!test.option3 || optMatch(v.option3, test.option3))
          );
        });

        if (!exists) {
          el.style.display = 'none';
          el.hidden = true;
          el.classList.add('buy-the-bundle__custom-dropdown-item--hidden');
          el.classList.add('buy-the-bundle__custom-dropdown-item--disabled');
          el.setAttribute('aria-disabled', 'true');
          el.setAttribute('data-available', 'false');
        } else {
          el.style.display = '';
          el.hidden = false;
          el.classList.remove('buy-the-bundle__custom-dropdown-item--hidden');
          textEl.textContent = available ? el.dataset.originalText : el.dataset.originalText + ' - Ausverkauft';
          el.classList.toggle('buy-the-bundle__custom-dropdown-item--disabled', !available);
          el.toggleAttribute('aria-disabled', !available);
          el.setAttribute('data-available', String(available));

          if (available) {
            if (!firstAvailableValue) firstAvailableValue = optValue;
            if (optValue === currentValue) hasAvailableSelected = true;
          }
        }
      }

      if (!hasAvailableSelected && firstAvailableValue && currentValue !== firstAvailableValue) {
        /** @type {HTMLInputElement} */ (hiddenInput).value = firstAvailableValue;
        selectedOptions[`option${pos}`] = firstAvailableValue;
        this.#updateDropdownDisplay(button, list, firstAvailableValue);
      }
    }
  }

  // --- Display updates ---

  /** @param {number} variantId */
  #updatePrimaryProductName(variantId) {
    const el = /** @type {HTMLElement | null} */ (this.refs.primaryProductName);
    if (!el) return;
    const nameData = this.#primaryVariantNames;
    const fullProd = this.#bundleFullProducts.get(0);
    const fullVariant = fullProd?.variants?.find((v) => v.id === variantId);
    const variantTitle = nameData?.variants?.get(variantId)
      || fullVariant?.variant_title
      || fullVariant?.variantTitle
      || (fullVariant?.name && fullVariant.name !== fullProd?.title && !fullVariant.name.includes(' - ') ? fullVariant.name : null)
      || fullVariant?.name
      || nameData?.productTitle
      || fullProd?.title
      || '';
    if (variantTitle) {
      el.textContent = variantTitle;
    }
  }

  /** @param {Variant} variant */
  #updatePrimaryProductPricing(variant) {
    const priceEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductPrice);
    const compareEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductComparePrice);
    const discountEl = /** @type {HTMLElement | null} */ (this.refs.primaryProductDiscount);

    if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);

    const estimatedRetail = this.#primaryEstimatedRetailPrices.get(variant.id) || 0;
    const strikeThroughPrice = (variant.compare_at_price && variant.compare_at_price > variant.price)
      ? variant.compare_at_price
      : (estimatedRetail > 0 ? estimatedRetail : (variant.compare_at_price || 0));
    const hasDiscount = strikeThroughPrice > variant.price;

    if (compareEl) {
      if (hasDiscount) {
        compareEl.innerHTML = `<span class="visually-hidden">Original price</span><s>${this.#formatMoney(strikeThroughPrice)}</s>`;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (discountEl) {
      if (hasDiscount) {
        const pct = Math.round(((strikeThroughPrice - variant.price) * 100) / strikeThroughPrice);
        discountEl.textContent = `(${pct}% ${Theme.translations?.off || 'Off'})`;
        discountEl.style.display = '';
      } else {
        discountEl.style.display = 'none';
      }
    }
  }

  /** @param {Variant} variant */
  #updatePrimaryProductDataset(variant) {
    const card = /** @type {HTMLElement | null} */ (this.refs.primaryProduct);
    if (!(card instanceof HTMLElement)) return;

    card.dataset.variantId = String(variant.id);
    card.dataset.price = String(variant.price || 0);
    card.dataset.comparePrice = String(variant.compare_at_price || 0);
    card.dataset.available = String(variant.available !== false);
    card.dataset.sku = variant.sku || '';
  }


  /**
   * @param {number} idx
   * @param {Variant} variant
   */
  #updateBundleProductDisplay(idx, variant) {
    const priceEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductPrice${idx}`]);
    const compareEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductComparePrice${idx}`]);
    const discountEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductDiscount${idx}`]);
    const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
    const nameEl = /** @type {HTMLElement | null} */ (this.refs[`bundleProductName${idx}`]);

    if (nameEl) {
      const nameData = this.#bundleVariantNames.get(idx);
      const fullProd = this.#bundleFullProducts.get(idx);
      const fullVariant = fullProd?.variants?.find((v) => v.id === variant.id);
      const variantTitle = nameData?.variants?.get(variant.id)
        || fullVariant?.variant_title
        || fullVariant?.variantTitle
        || (fullVariant?.name && fullVariant.name !== fullProd?.title && !fullVariant.name.includes(' - ') ? fullVariant.name : null)
        || fullVariant?.name
        || nameData?.productTitle
        || fullProd?.title
        || '';
      if (variantTitle) {
        nameEl.textContent = variantTitle;
      }
    }

    if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);

    const retailMap = this.#bundleEstimatedRetailPrices.get(idx);
    const estimatedRetail = retailMap?.get(variant.id) || 0;
    const strikePrice = (variant.compare_at_price && variant.compare_at_price > variant.price)
      ? variant.compare_at_price
      : (estimatedRetail > 0 ? estimatedRetail : (variant.compare_at_price || 0));
    const hasDiscount = strikePrice > variant.price;

    if (compareEl) {
      if (hasDiscount) {
        compareEl.innerHTML = `<span class="visually-hidden">Original price</span><s>${this.#formatMoney(strikePrice)}</s>`;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (discountEl) {
      if (hasDiscount) {
        const pct = Math.round(((strikePrice - variant.price) * 100) / strikePrice);
        discountEl.textContent = `(${pct}% ${Theme.translations?.off || 'Off'})`;
        discountEl.style.display = '';
      } else {
        discountEl.style.display = 'none';
      }
    }

    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs[`bundleImageWrapper${idx}`]), variant, idx);
    this.#updateProductImage(/** @type {HTMLElement | null} */ (this.refs[`mobileBundleImageWrapper${idx}`]), variant, idx);

    if (card instanceof HTMLElement) {
      card.dataset.variantId = String(variant.id);
      card.dataset.price = String(variant.price);
      card.dataset.comparePrice = String(variant.compare_at_price || 0);
    }
  }

  /**
   * @param {HTMLElement | null} wrapper
   * @param {Variant} variant
   * @param {number} [idx=0]
   */
  #updateProductImage(wrapper, variant, idx = 0) {
    if (!variant) return;

    /** @type {Element[]} */
    const targetWrappers = [];
    if (wrapper) {
      if (Array.isArray(wrapper)) targetWrappers.push(...wrapper);
      else targetWrappers.push(wrapper);
    }

    // Add DOM fallbacks for both desktop and mobile
    if (idx === 0) {
      const desktopEl = this.querySelector('.buy-the-bundle__product-card--primary .buy-the-bundle__product-image-wrapper');
      const mobileEl = this.querySelector('.buy-the-bundle__mobile-image-wrapper--primary');
      if (desktopEl && !targetWrappers.includes(desktopEl)) targetWrappers.push(desktopEl);
      if (mobileEl && !targetWrappers.includes(mobileEl)) targetWrappers.push(mobileEl);
    } else if (idx > 0) {
      const desktopEl = this.querySelector(`.buy-the-bundle__product-card[data-bundle-index="${idx}"] .buy-the-bundle__product-image-wrapper`);
      const mobileEl = this.querySelector(`.buy-the-bundle__mobile-image-wrapper[data-bundle-index="${idx}"]`);
      if (desktopEl && !targetWrappers.includes(desktopEl)) targetWrappers.push(desktopEl);
      if (mobileEl && !targetWrappers.includes(mobileEl)) targetWrappers.push(mobileEl);
    }

    let src = '';
    let alt = '';

    // 1. From metadata parsed images
    if (idx === 0) {
      src = this.#primaryVariantImages.get(variant.id) || '';
    } else if (idx > 0) {
      src = this.#bundleVariantImages.get(idx)?.get(variant.id) || '';
    }

    // 2. From featured_image
    if (!src) {
      const fi = variant.featured_image;
      if (typeof fi === 'string') {
        src = fi;
      } else if (fi && typeof fi === 'object') {
        src = fi.src || /** @type {any} */ (fi).url || '';
        alt = fi.alt || '';
      }
    }

    // 3. From featured_media
    if (!src && /** @type {any} */ (variant).featured_media) {
      const fm = /** @type {any} */ (variant).featured_media;
      if (typeof fm === 'string') {
        src = fm;
      } else if (fm && typeof fm === 'object') {
        src = fm.preview_image?.src || (typeof fm.preview_image === 'string' ? fm.preview_image : '') || fm.src || '';
        alt = fm.alt || '';
      }
    }

    // 4. From image property
    if (!src && /** @type {any} */ (variant).image) {
      const vi = /** @type {any} */ (variant).image;
      if (typeof vi === 'string') {
        src = vi;
      } else if (vi && typeof vi === 'object') {
        src = vi.src || '';
        alt = vi.alt || '';
      }
    }

    // 5. Fallback: Check if dropdown item has data-image for this variant ID
    if (!src && variant.id) {
      const dropdownItem = this.querySelector(`.buy-the-bundle__custom-dropdown-item[data-variant-id="${variant.id}"]`);
      if (dropdownItem) {
        src = dropdownItem.getAttribute('data-image') || '';
      }
    }

    // 6. Fallback: Check first option matching swatch data-image if variant has option1
    if (!src && variant.option1) {
      const dropdownItem = this.querySelector(`.buy-the-bundle__custom-dropdown-item[data-value="${CSS.escape(variant.option1)}"][data-image]`);
      if (dropdownItem) {
        src = dropdownItem.getAttribute('data-image') || '';
      }
    }

    if (src) {
      if (src.startsWith('//')) {
        src = `https:${src}`;
      }
      if (!src.includes('width=')) {
        src = src.includes('?') ? `${src}&width=200` : `${src}?width=200`;
      }
    }

    for (const targetWrapper of targetWrappers) {
      if (!(targetWrapper instanceof HTMLElement)) continue;

      const img = targetWrapper.querySelector('img');
      if (img instanceof HTMLImageElement && src) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
        if (alt || /** @type {any} */ (variant).title) img.alt = alt || /** @type {any} */ (variant).title || '';
      }

      const trigger = targetWrapper.matches('[data-quick-view-trigger]')
        ? targetWrapper
        : targetWrapper.querySelector('[data-quick-view-trigger]');
      if (trigger instanceof HTMLElement && variant.id) {
        trigger.dataset.variantId = String(variant.id);
      }
    }
  }

  #updatePrices() {
    const totalPrice = /** @type {HTMLElement | null} */ (this.refs.totalPrice);
    const separatelyPrice = /** @type {HTMLElement | null} */ (this.refs.separatelyPrice);
    const bundleSavingPrice = /** @type {HTMLElement | null} */ (this.refs.bundleSavingPrice);
    const bundleSavingRow = /** @type {HTMLElement | null} */ (this.refs.bundleSavingRow);
    const separatelyRow = /** @type {HTMLElement | null} */ (this.refs.separatelyRow);
    const discountBadgeText = /** @type {HTMLElement | null} */ (this.refs.discountBadgeText);
    const summaryItemCount = /** @type {HTMLElement | null} */ (this.refs.summaryItemCount);
    const headerSavedAmount = /** @type {HTMLElement | null} */ (this.refs.headerSavedAmount);
    const headerSavedAmountRow = /** @type {HTMLElement | null} */ (this.refs.headerSavedAmountRow);
    const addToCartText = /** @type {HTMLElement | null} */ (this.refs.addToCartText);

    let selectedCount = 1;
    let totalBase = this.#primaryPrice;

    const primaryVariant = this.#primaryVariants.find((v) => v.id === this.#primaryVariantId);
    const primaryEstimatedRetail = this.#primaryEstimatedRetailPrices.get(this.#primaryVariantId) || 0;
    const primaryCard = /** @type {HTMLElement | null} */ (this.refs.primaryProduct);
    const primaryCompareFromDataset = parseInt(primaryCard?.dataset.comparePrice || '0', 10);

    let primaryStrike = (primaryVariant?.compare_at_price && primaryVariant.compare_at_price > this.#primaryPrice)
      ? primaryVariant.compare_at_price
      : (primaryEstimatedRetail > this.#primaryPrice ? primaryEstimatedRetail : (primaryCompareFromDataset > this.#primaryPrice ? primaryCompareFromDataset : this.#primaryPrice));

    let totalComparePrice = primaryStrike;

    for (const [idx, bp] of this.#bundleProducts) {
      if (bp.isSelected) {
        selectedCount++;
        totalBase += bp.price;

        const variants = this.#bundleVariants.get(idx);
        const variant = variants?.find((v) => v.id === bp.variantId);
        const estimatedRetail = this.#bundleEstimatedRetailPrices.get(idx)?.get(bp.variantId) || 0;
        const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${idx}`]);
        const compareFromDataset = parseInt(card?.dataset.comparePrice || '0', 10);

        const strike = (variant?.compare_at_price && variant.compare_at_price > bp.price)
          ? variant.compare_at_price
          : (estimatedRetail > bp.price ? estimatedRetail : (compareFromDataset > bp.price ? compareFromDataset : bp.price));

        totalComparePrice += strike;
      }
    }

    let pct = 0;

    if (selectedCount >= 3) {
      pct = this.#discount3 || this.#discount2;
    } else if (selectedCount >= 2) {
      pct = this.#discount2;
    }

    const discountedTotal = Math.round(totalBase * ((100 - pct) / 100));
    const bundleDiscountSaving = Math.max(totalBase - discountedTotal, 0);
    const totalOverallSaving = Math.max(totalComparePrice - discountedTotal, 0);

    if (summaryItemCount) {
      summaryItemCount.textContent = String(selectedCount);
    }

    if (separatelyPrice) {
      separatelyPrice.textContent = this.#formatMoney(totalBase);
    }

    if (bundleSavingPrice) {
      bundleSavingPrice.textContent = this.#formatMoney(bundleDiscountSaving);
    }

    if (bundleSavingRow) {
      if (bundleDiscountSaving > 0) {
        bundleSavingRow.style.display = '';
      } else {
        bundleSavingRow.style.display = 'none';
      }
    }

    if (totalPrice) {
      totalPrice.textContent = this.#formatMoney(discountedTotal);
    }

    if (headerSavedAmount) {
      headerSavedAmount.textContent = this.#formatMoney(bundleDiscountSaving);
    }

    if (headerSavedAmountRow) {
      if (selectedCount >= 2 && bundleDiscountSaving > 0) {
        headerSavedAmountRow.style.removeProperty('display');
      } else {
        headerSavedAmountRow.style.display = 'none';
      }
    }

    if (discountBadgeText) {
      const fullDiscount = this.#bundleProducts.size >= 2 ? (this.#discount3 || this.#discount2) : this.#discount2;
      const displayDiscount = fullDiscount > 0 ? fullDiscount : 10;
      discountBadgeText.innerHTML = `<span class="buy-the-bundle__discount-badge-percent">${displayDiscount}%</span> <span class="buy-the-bundle__discount-badge-extra">Extra-</span>Rabatt`;
      discountBadgeText.parentElement?.style.setProperty('display', '');
    }

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    const totalCountWrapper = /** @type {HTMLElement | null} */ (this.refs.totalCountWrapper);

    const fullBundleDiscount = this.#bundleProducts.size >= 2 ? (this.#discount3 || this.#discount2) : this.#discount2;
    const targetDiscount = fullBundleDiscount > 0 ? fullBundleDiscount : 10;

    if (selectedCount === 1) {
      // Unchecked state (only primary product selected)
      if (separatelyRow) separatelyRow.style.display = 'none';
      if (bundleSavingRow) bundleSavingRow.style.display = 'none';
      if (totalCountWrapper) totalCountWrapper.style.display = 'none';

      if (addToCartButton) {
        addToCartButton.classList.remove('buy-the-bundle__add-to-cart--disabled-state');
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('aria-disabled');
      }

      if (addToCartText) {
        addToCartText.textContent = `In den Warenkorb`;
      }
    } else {
      // Bundle active state (2 or 3 products selected)
      if (separatelyRow) separatelyRow.style.display = '';
      if (totalCountWrapper) totalCountWrapper.style.display = '';

      if (addToCartButton) {
        addToCartButton.classList.remove('buy-the-bundle__add-to-cart--disabled-state');
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('aria-disabled');
      }

      if (addToCartText) {
        if (pct > 0) {
          addToCartText.textContent = `In den Warenkorb | Save ${pct}%`;
        } else {
          addToCartText.textContent = `In den Warenkorb`;
        }
      }
    }

    this.#updateItemCount();
  }

  #updateItemCount() {
    let count = 1;
    for (const [, bp] of this.#bundleProducts) { if (bp.isSelected) count++; }
    let itemCount = /** @type {HTMLElement | null} */ (this.refs.itemCount);
    if (itemCount) itemCount.textContent = String(count);

    let summaryItemCount = /** @type {HTMLElement | null} */ (this.refs.summaryItemCount);
    if (summaryItemCount) summaryItemCount.textContent = String(count);
  }

  /** @param {boolean} visible */
  #setBundleVisibility(visible) {
    this.style.display = visible ? '' : 'none';
    this.toggleAttribute('hidden', !visible);
  }

  // --- Mobile toggle ---

  /** @param {AbortSignal} signal */
  #initMobileToggle(signal) {
    const mobileToggle = /** @type {HTMLElement | null} */ (this.refs.mobileToggle);
    const contentWrapper = /** @type {HTMLElement | null} */ (this.refs.contentWrapper);
    if (!mobileToggle || !contentWrapper) return;

    mobileToggle.addEventListener('click', () => {
      const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
      mobileToggle.setAttribute('aria-expanded', String(!expanded));
      contentWrapper.classList.toggle('is-expanded', !expanded);
      this.classList.toggle('is-mobile-expanded', !expanded);
    }, { signal });
  }

  // --- Checkboxes ---

  /** @param {AbortSignal} signal */
  /** @param {AbortSignal} signal */
  #initCheckboxes(signal) {
    const syncCheckboxUi = (i) => {
      const checkbox = /** @type {HTMLInputElement | null} */ (this.refs[`bundleCheckbox${i}`]);
      const bp = this.#bundleProducts.get(i);
      if (!checkbox || !bp) return;

      checkbox.checked = bp.isSelected;

      const card = /** @type {HTMLElement | null} */ (this.refs[`bundleProduct${i}`]);
      card?.classList.toggle('buy-the-bundle__product-card--selected', bp.isSelected);

      const mobileImg = /** @type {HTMLElement | null} */ (this.refs[`mobileBundleImageWrapper${i}`]);
      mobileImg?.classList.toggle('buy-the-bundle__mobile-image-wrapper--selected', bp.isSelected);
    };

    const syncAllCheckboxesUi = () => {
      for (let i = 1; i <= 2; i++) {
        syncCheckboxUi(i);
      }

      this.#updatePrices();
    };

    for (let i = 1; i <= 2; i++) {
      const checkbox = /** @type {HTMLInputElement | null} */ (this.refs[`bundleCheckbox${i}`]);
      if (!checkbox) continue;

      checkbox.addEventListener('change', () => {
        const bp = this.#bundleProducts.get(i);
        if (bp) bp.isSelected = checkbox.checked;

        syncCheckboxUi(i);
        this.#updatePrices();
      }, { signal });

      syncCheckboxUi(i);
    }

    window.addEventListener('pageshow', () => {
      syncAllCheckboxesUi();
    }, { signal });

    syncAllCheckboxesUi();
  }

  // --- Custom dropdowns ---

  /** @param {AbortSignal} signal */
  #initCustomDropdowns(signal) {
    this.addEventListener(
      'click',
      (e) => {
        const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
        if (!target) return;

        // 1. Button click -> toggle dropdown
        const button = target.closest('.buy-the-bundle__custom-dropdown-button');
        if (button && this.contains(button)) {
          e.preventDefault();
          e.stopPropagation();
          const wrapper = button.closest('.buy-the-bundle__custom-dropdown-wrapper');
          const list = wrapper?.querySelector('.buy-the-bundle__custom-dropdown-list');
          if (list) {
            this.#toggleDropdown(button, list);
          }
          return;
        }

        // 2. Dropdown item click -> select item
        const item = target.closest('.buy-the-bundle__custom-dropdown-item');
        if (item && this.contains(item)) {
          e.preventDefault();
          e.stopPropagation();
          if (item.classList.contains('buy-the-bundle__custom-dropdown-item--disabled')) return;

          const wrapper = item.closest('.buy-the-bundle__custom-dropdown-wrapper');
          const buttonEl = wrapper?.querySelector('.buy-the-bundle__custom-dropdown-button');
          const listEl = wrapper?.querySelector('.buy-the-bundle__custom-dropdown-list');
          const hiddenInput = wrapper?.querySelector('.buy-the-bundle__custom-dropdown-input');

          if (buttonEl && listEl && hiddenInput) {
            this.#selectDropdownItem(buttonEl, listEl, hiddenInput, item);
          }
          return;
        }
      },
      { signal },
    );

    // Also attach keyboard listeners per wrapper
    for (const wrapper of this.querySelectorAll('.buy-the-bundle__custom-dropdown-wrapper')) {
      const button = wrapper.querySelector('.buy-the-bundle__custom-dropdown-button');
      const list = wrapper.querySelector('.buy-the-bundle__custom-dropdown-list');
      const hiddenInput = wrapper.querySelector('.buy-the-bundle__custom-dropdown-input');
      if (!button || !list || !hiddenInput) continue;

      button.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
          e.preventDefault();
          if (button.getAttribute('aria-expanded') !== 'true') this.#toggleDropdown(button, list);
        } else if (e.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          button.setAttribute('aria-expanded', 'false');
          list.setAttribute('hidden', '');
        }
      }, { signal });

      list.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        this.#handleDropdownListKeydown(e, button, list, hiddenInput);
      }, { signal });
    }

    document.addEventListener('click', () => this.#closeAllDropdowns(), { signal });
  }

  /**
   * Reorders variant dropdown options so Color/Config options come first and Size comes second.
   */
  #reorderVariantOptions() {
    this.querySelectorAll('.buy-the-bundle__variant-option').forEach((optDiv) => {
      const button = optDiv.querySelector('.buy-the-bundle__custom-dropdown-button');
      const rawName = (button?.getAttribute('data-option-name') || '').toLowerCase();
      const firstVal = optDiv.querySelector('.buy-the-bundle__custom-dropdown-item')?.getAttribute('data-value') || '';

      const isColor =
        rawName.includes('color') ||
        rawName.includes('colour') ||
        rawName.includes('farbe') ||
        rawName.includes('config') ||
        rawName.includes('material') ||
        rawName.includes('metall') ||
        rawName.includes('ausführung');

      const isSizeName =
        rawName.includes('size') ||
        rawName.includes('größe') ||
        rawName.includes('groesse') ||
        rawName.includes('grösse') ||
        rawName.includes('ring') ||
        rawName.includes('weite') ||
        rawName.includes('abmessung');

      const isNumericOrSizeVal =
        !isColor &&
        (/^\d+(\.\d+)?$/.test(firstVal.trim()) ||
          /^(xs|s|m|l|xl|xxl)$/i.test(firstVal.trim()) ||
          /^\d+\s*cm$/i.test(firstVal.trim()));

      const isSize = (isSizeName || isNumericOrSizeVal) && !isColor;

      optDiv.setAttribute('data-is-size', String(isSize));
      optDiv.querySelectorAll('.buy-the-bundle__custom-dropdown-wrapper').forEach((w) => {
        w.setAttribute('data-is-size', String(isSize));
      });

      if (isSize) {
        /** @type {HTMLElement} */ (optDiv).style.setProperty('order', '2', 'important');
      } else {
        /** @type {HTMLElement} */ (optDiv).style.setProperty('order', '1', 'important');
      }
    });
  }

  /**
   * @param {Element} button
   * @param {Element} list
   */
  #toggleDropdown(button, list) {
    const wrapper = button.closest('.buy-the-bundle__custom-dropdown-wrapper');
    const idx = parseInt(wrapper instanceof HTMLElement ? (wrapper.dataset.bundleIndex || '0') : '0', 10);
    const variants = idx === 0 ? this.#primaryVariants : this.#bundleVariants.get(idx);
    if (variants && variants.length > 0) {
      const inputs = this.#getOptionInputs(idx);
      this.#updateOptionAvailability(idx, this.#readSelectedOptions(inputs), variants);
    }

    const open = button.getAttribute('aria-expanded') === 'true';
    this.#closeAllDropdowns();
    if (!open) {
      button.setAttribute('aria-expanded', 'true');
      list.removeAttribute('hidden');
      const target =
        list.querySelector('.buy-the-bundle__custom-dropdown-item--selected:not([hidden]):not([style*="display: none"])') ||
        list.querySelector('.buy-the-bundle__custom-dropdown-item:not(.buy-the-bundle__custom-dropdown-item--disabled):not([hidden]):not([style*="display: none"])');
      if (target instanceof HTMLElement) target.focus();
    }
  }

  #closeAllDropdowns() {
    for (const btn of this.querySelectorAll('.buy-the-bundle__custom-dropdown-button[aria-expanded="true"]')) {
      btn.setAttribute('aria-expanded', 'false');
    }
    for (const lst of this.querySelectorAll('.buy-the-bundle__custom-dropdown-list:not([hidden])')) {
      lst.setAttribute('hidden', '');
    }
  }

  /**
   * @param {Element} button
   * @param {Element} list
   * @param {Element} hiddenInput
   * @param {Element} item
   */
  #selectDropdownItem(button, list, hiddenInput, item) {
    const value = item.getAttribute('data-value') || '';
    const variantIdAttr = item.getAttribute('data-variant-id') || '';
    if (hiddenInput instanceof HTMLInputElement) hiddenInput.value = value;

    this.#updateDropdownDisplay(button, list, value);

    button.setAttribute('aria-expanded', 'false');
    list.setAttribute('hidden', '');
    if (button instanceof HTMLElement) button.focus();

    if (hiddenInput instanceof HTMLInputElement) {
      this.#handleVariantChange(hiddenInput, variantIdAttr ? parseInt(variantIdAttr, 10) : undefined);
    }
  }

  /**
   * Shared helper: updates button text, swatch preview, and selected state in list.
   * @param {Element} button
   * @param {Element} list
   * @param {string} value
   */
  #updateDropdownDisplay(button, list, value) {
    const item = list.querySelector(`.buy-the-bundle__custom-dropdown-item[data-value="${CSS.escape(value)}"]`);
    const image = item?.getAttribute('data-image') || '';

    const selectedValueSpan = button.querySelector('.buy-the-bundle__selected-value');
    if (selectedValueSpan) {
      selectedValueSpan.textContent = value;
    } else {
      const text = button.querySelector('.buy-the-bundle__custom-dropdown-text');
      if (text) {
        text.textContent = '';

        const span = document.createElement('span');
        span.className = 'buy-the-bundle__selected-value';
        span.textContent = value;

        text.appendChild(span);
      }
    }

    const swatchPreview = button.querySelector('.buy-the-bundle__swatch-preview');
    if (swatchPreview) {
      const img = swatchPreview.querySelector('img');
      if (img instanceof HTMLImageElement && image) {
        img.src = image;
        img.alt = value;
        img.style.display = '';
      } else if (!image && img instanceof HTMLImageElement) {
        img.style.display = 'none';
      } else if (image) {
        swatchPreview.innerHTML = `<img src="${image}" alt="${value}" width="24" height="24" loading="lazy">`;
      }
    }

    for (const li of list.querySelectorAll('.buy-the-bundle__custom-dropdown-item')) {
      li.classList.remove('buy-the-bundle__custom-dropdown-item--selected');
      li.removeAttribute('aria-selected');
    }
    if (item) {
      item.classList.add('buy-the-bundle__custom-dropdown-item--selected');
      item.setAttribute('aria-selected', 'true');
    }
  }

  /**
   * @param {KeyboardEvent} e
   * @param {Element} button
   * @param {Element} list
   * @param {Element} hiddenInput
   */
  #handleDropdownListKeydown(e, button, list, hiddenInput) {
    const items = Array.from(
      list.querySelectorAll('.buy-the-bundle__custom-dropdown-item:not(.buy-the-bundle__custom-dropdown-item--disabled)'),
    );
    const cur = items.findIndex((i) => i === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = items[cur + 1];
        if (next instanceof HTMLElement) next.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = items[cur - 1];
        if (prev instanceof HTMLElement) prev.focus();
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (cur >= 0 && items[cur]) this.#selectDropdownItem(button, list, hiddenInput, items[cur]);
        break;
      case 'Escape':
        e.preventDefault();
        button.setAttribute('aria-expanded', 'false');
        list.setAttribute('hidden', '');
        if (button instanceof HTMLElement) button.focus();
        break;
      case 'Tab':
        button.setAttribute('aria-expanded', 'false');
        list.setAttribute('hidden', '');
        break;
    }
  }

  // --- Add to cart ---

  /** @param {Event} event */
  async #handleAddToCart(event) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (!addToCartButton) return;

    if (
      addToCartButton.classList.contains('buy-the-bundle__add-to-cart--disabled-state') ||
      addToCartButton.getAttribute('aria-disabled') === 'true'
    ) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }

    /** @type {Array<{ variantId: number; quantity: number; price: number; sku: string }>} */
    const selectedBundleItems = [];
    for (const [, bp] of this.#bundleProducts) {
      if (bp.isSelected && bp.variantId) {
        selectedBundleItems.push({ variantId: bp.variantId, quantity: 1, price: bp.price, sku: bp.sku });
      }
    }

    addToCartButton.disabled = true;
    this.#animateAddToCart();

    const totalProductCount = 1 + selectedBundleItems.length;
    let discountPct = 0;
    if (totalProductCount >= 3 && this.#discount3 > 0) discountPct = this.#discount3;
    else if (totalProductCount >= 2 && this.#discount2 > 0) discountPct = this.#discount2;

    const discountMultiplier = (100 - discountPct) / 100;

    /** @type {Record<string, any>} */
    const properties = {};

    if (selectedBundleItems.length > 0) {
      /** @type {string} */
      const uuid = Date.now().toString();

      /** @type {Array<{ id: string; sku: string; qty: number; price: string; is_main: boolean; timestamp: string }>} */
      const group = [
        {
          id: String(this.#primaryVariantId),
          sku: this.#primarySku,
          qty: 1,
          price: String(Math.round(this.#primaryPrice * discountMultiplier)),
          is_main: true,
          timestamp: uuid,
        },
      ];

      for (const item of selectedBundleItems) {
        group.push({
          id: String(item.variantId),
          sku: item.sku,
          qty: item.quantity,
          price: String(Math.round(item.price * discountMultiplier)),
          is_main: false,
          timestamp: uuid,
        });
      }

      properties._group = JSON.stringify(group);
    }

    const hasProperties = Object.keys(properties).length > 0;

    /** @type {Array<{ id: number; quantity: number; properties?: Record<string, any> }>} */
    const items = [{
      id: this.#primaryVariantId,
      quantity: 1,
      ...(hasProperties && { properties }),
    }];

    const sectionIds = /** @type {string[]} */ ([]);
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    /** @type {{ items: typeof items; sections?: string }} */
    const payload = { items };
    if (sectionIds.length > 0) payload.sections = sectionIds.join(',');

    try {
      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await res.json();
      if (data.status) {
        this.#showAddToCartError(data.message || 'Error adding to cart');
        this.dispatchEvent(new CartErrorEvent('buy-the-bundle', data.message, data.description, data.errors));
      } else {
        this.dispatchEvent(new CartAddEvent({}, String(this.#primaryVariantId), {
          source: 'buy-the-bundle', itemCount: totalProductCount, productId: this.dataset.productId, sections: data.sections,
        }));
        setTimeout(() => { addToCartButton.disabled = false; }, 500);
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      this.#showAddToCartError('An error occurred. Please try again.');
      addToCartButton.disabled = false;
    } finally {
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /** @param {string} msg */
  #showAddToCartError(msg) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    const errorEl = /** @type {HTMLElement | null} */ (this.refs.addToCartTextError);

    if (errorEl) {
      errorEl.classList.remove('hidden');
      let span = errorEl.querySelector('.product-form-error-text');
      if (!span) {
        span = document.createElement('span');
        span.classList.add('product-form-error-text');
        errorEl.appendChild(span);
      }
      span.textContent = msg;

      if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
      this.#errorTimeout = setTimeout(() => {
        errorEl.classList.add('hidden');
        const el = errorEl.querySelector('.product-form-error-text');
        if (el) el.textContent = '';
      }, ERROR_DISPLAY_MS);
    }

    setTimeout(() => { if (addToCartButton) addToCartButton.disabled = false; }, ERROR_REENABLE_MS);
  }

  #animateAddToCart = async function () {
    const btn = /** @type {HTMLButtonElement | null} */ (this.refs.addToCartButton);
    if (!btn) return;
    for (const id of this.#resetTimeouts) clearTimeout(id);
    this.#resetTimeouts = [];
    if (btn.dataset.added !== 'true') btn.dataset.added = 'true';
    await onAnimationEnd(btn);
    const tid = setTimeout(() => {
      btn.removeAttribute('data-added');
      const idx = this.#resetTimeouts.indexOf(tid);
      if (idx > -1) this.#resetTimeouts.splice(idx, 1);
    }, 800);
    this.#resetTimeouts.push(tid);
  };

  // --- Dedicated Product Popout Modal ---

  /** @param {AbortSignal} signal */
  #initQuickViewTriggers(signal) {
    const handleTriggerClick = (event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      if (!target) return;

      if (target.closest('[data-close-btb-modal]') || target.classList.contains('btb-modal__backdrop') || target.classList.contains('btb-modal')) {
        event.preventDefault();
        event.stopPropagation();
        this.#closeProductModal();
        return;
      }

      if (target.closest('.btb-modal__dialog')) {
        return;
      }

      if (target.closest('input, select, button, .buy-the-bundle__custom-dropdown-wrapper, .buy-the-bundle__checkbox-overlay')) {
        return;
      }

      const trigger = target.closest('[data-quick-view-trigger]');
      if (!trigger || !(trigger instanceof HTMLElement)) {
        return;
      }

      const card = trigger.closest('.buy-the-bundle__product-card') || trigger.closest('.buy-the-bundle__mobile-image-wrapper');
      if (!card || !(card instanceof HTMLElement)) {
        return;
      }

      // Primary product should NEVER open the modal
      if (card.classList.contains('buy-the-bundle__product-card--primary') || card.classList.contains('buy-the-bundle__mobile-image-wrapper--primary')) {
        return;
      }

      const bundleIndex = card.dataset.bundleIndex
        ? parseInt(card.dataset.bundleIndex, 10)
        : (trigger.dataset.bundleIndex ? parseInt(trigger.dataset.bundleIndex, 10) : 0);

      if (bundleIndex === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      let productHandle = card.dataset.productHandle || trigger.dataset.productHandle || '';
      let variantId = card.dataset.variantId || trigger.dataset.variantId || '';

      const bp = this.#bundleProducts.get(bundleIndex);
      if (bp) {
        productHandle = bp.productHandle || productHandle;
        variantId = String(bp.variantId || variantId);
      }

      this.#openProductModal(bundleIndex, productHandle, variantId);
    };

    this.addEventListener('click', handleTriggerClick, { signal });

    document.addEventListener('click', (event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      if (!target) return;

      if (target.closest('[data-close-btb-modal]') || target.classList.contains('btb-modal__backdrop') || (target.classList.contains('btb-modal') && !target.closest('.btb-modal__dialog'))) {
        event.preventDefault();
        event.stopPropagation();
        this.#closeProductModal();
      }
    }, { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          this.#closeProductModal();
        }
      },
      { signal }
    );
  }

  #closeProductModal() {
    const modals = document.querySelectorAll('.btb-modal');
    modals.forEach((modal) => {
      modal.classList.remove('is-open');
      if (modal instanceof HTMLElement) {
        modal.style.display = 'none';
      }
      if (typeof modal['close'] === 'function') {
        try {
          modal['close']();
        } catch { /* ignore */ }
      }
    });
    document.body.style.overflow = '';
  }

  /**
   * Opens the custom product popup dialog modal with full details, image gallery, and option selectors.
   * @param {number} bundleIndex
   * @param {string} productHandle
   * @param {string} [initialVariantId]
   */
  async #openProductModal(bundleIndex, productHandle, initialVariantId) {
    let modal = /** @type {HTMLElement | null} */ (document.querySelector('.btb-modal') || this.querySelector('.btb-modal') || this.refs.productModal);
    
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const modalBody = /** @type {HTMLElement | null} */ (modal?.querySelector('.btb-modal__body') || this.querySelector('.btb-modal__body') || this.refs.modalBody);

    if (!modal || !modalBody) {
      console.error('[BuyTheBundle] Modal container or modal body not found in DOM!', { modal, modalBody });
      return;
    }

    const updateHeaderOffset = () => {
      // Prioritize the sticky header bar element to get the exact sticky header height (~54px-66px)
      // rather than the entire unscrolled header group with announcements/banners (~126px)
      const headerEl = document.querySelector('.header-new__inner')
        || document.querySelector('.header-new__container')
        || document.querySelector('.header-new')
        || document.querySelector('header-component')
        || document.querySelector('.sticky-header')
        || document.querySelector('.site-header')
        || document.querySelector('header');
      let headerBottom = 0;
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        headerBottom = Math.max(0, rect.bottom);
      }
      modal.style.setProperty('--btb-header-offset', `${headerBottom}px`);
    };
    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset, { passive: true });

    modal.style.display = 'flex';
    modal.classList.add('is-open');
    if (typeof modal.showModal === 'function') {
      try {
        if (!modal.open) modal.showModal();
      } catch { /* ignore */ }
    }
    document.body.style.overflow = 'hidden';

    const accordionCache = new Map();
    const pdpTitleCache = new Map();
    const pdpReviewsCache = new Map();

    const fetchVariantAccordion = async (vId) => {
      const variantId = vId || initialVariantId;
      if (variantId && accordionCache.has(variantId)) {
        return {
          html: accordionCache.get(variantId) || '',
          title: pdpTitleCache.get(variantId) || '',
          reviewsHtml: pdpReviewsCache.get(variantId) || ''
        };
      }
      try {
        if (!productHandle) return { html: '', title: '', reviewsHtml: '' };
        const url = variantId ? `/products/${productHandle}?variant=${variantId}` : `/products/${productHandle}`;
        const htmlRes = await fetch(url);
        if (!htmlRes.ok) throw new Error(`HTTP error ${htmlRes.status}`);
        const htmlText = await htmlRes.text();
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');

        const titleEl = doc.querySelector('a[ref="productTitleLink"] p, a[ref="productTitleLink"], variant-picker[data-variant-title], [data-variant-title], .product-title, .product__title');
        const liveTitle = titleEl?.getAttribute('data-variant-title') || titleEl?.textContent?.trim() || '';
        if (variantId && liveTitle) {
          pdpTitleCache.set(variantId, liveTitle);
        }

        const reviewsEl = doc.querySelector('.desktop-sticky-atc__reviews');
        let liveReviewsHtml = '';
        if (reviewsEl) {
          liveReviewsHtml = reviewsEl.outerHTML;
          if (variantId) {
            pdpReviewsCache.set(variantId, liveReviewsHtml);
          }
        }

        const accEl = doc.querySelector('.pdp-accordion, [data-pdp-accordion]');
        let html = '';
        if (accEl) {
          const dd = accEl.querySelector('#productDropdown');
          if (dd) dd.remove();
          html = accEl.outerHTML;
          if (variantId) accordionCache.set(variantId, html);
        }
        return { html, title: liveTitle, reviewsHtml: liveReviewsHtml };
      } catch (e) {
        console.error('[BuyTheBundle] Live PDP fetch error:', e);
      }
      return { html: '', title: '', reviewsHtml: '' };
    };

    // 1. Check preloaded product data first for instant opening without network latency
    const preloaded = this.#bundleFullProducts.get(bundleIndex);

    if (preloaded && preloaded.variants && preloaded.variants.length > 0) {
      let selectedVariant = preloaded.variants.find((/** @type {any} */ v) => String(v.id) === String(initialVariantId));
      if (!selectedVariant) {
        selectedVariant = preloaded.variants.find((/** @type {any} */ v) => v.available) || preloaded.variants[0];
      }
      this.#renderModalContent(modalBody, preloaded, selectedVariant, bundleIndex, fetchVariantAccordion, accordionCache, pdpTitleCache, pdpReviewsCache);
      return;
    }

    // 2. Fetch from /products/${productHandle}.js if not preloaded
    modalBody.innerHTML = `
      <div class="btb-modal__loading">
        <div class="btb-modal__spinner"></div>
      </div>
    `;

    try {
      if (!productHandle) throw new Error('No product handle provided');
      const product = await fetch(`/products/${productHandle}.js`).then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.json();
      });

      let selectedVariant = product.variants.find((/** @type {any} */ v) => String(v.id) === String(initialVariantId));
      if (!selectedVariant) {
        selectedVariant = product.variants.find((/** @type {any} */ v) => v.available) || product.variants[0];
      }

      this.#renderModalContent(modalBody, product, selectedVariant, bundleIndex, fetchVariantAccordion, accordionCache, pdpTitleCache, pdpReviewsCache);
    } catch (err) {
      console.error('[BuyTheBundle] Modal product load error:', err);
      modalBody.innerHTML = `
        <div style="padding: 40px 20px; text-align: center; color: #8c0008; font-size: 15px;">
          Fehler beim Laden des Produkts (${productHandle || 'Unbekannt'}). Bitte versuchen Sie es erneut.
        </div>
      `;
    }
  }

  /**
   * Renders the product details, gallery, and variant selectors inside the modal.
   * @param {HTMLElement} container
   * @param {any} product
   * @param {any} initialVariant
   * @param {number} [bundleIndex]
   * @param {(variantId: number | string) => Promise<{ html: string, title: string, reviewsHtml?: string }>} [fetchVariantAccordion]
   * @param {Map<any, string>} [accordionCache]
   * @param {Map<any, string>} [pdpTitleCache]
   * @param {Map<any, string>} [pdpReviewsCache]
   */
    #renderModalContent(container, product, initialVariant, bundleIndex = 1, fetchVariantAccordion = null, accordionCache = new Map(), pdpTitleCache = new Map(), pdpReviewsCache = new Map()) {
    let currentVariant = initialVariant;

    const normalizeImg = (img) => {
      if (!img) return '';
      if (typeof img === 'string') {
        return img.startsWith('//') ? `https:${img}` : img;
      }
      if (typeof img === 'object' && img.src) {
        return img.src.startsWith('//') ? `https:${img.src}` : img.src;
      }
      return '';
    };

    const rawImages = Array.isArray(product.images) && product.images.length > 0
      ? product.images
      : (product.featured_image ? [product.featured_image] : []);
    const images = rawImages.map(normalizeImg).filter(Boolean);

    let activeImageIndex = 0;
    const varImg = normalizeImg(currentVariant?.featured_image);
    if (varImg) {
      const idx = images.findIndex((img) => img.includes(varImg) || varImg.includes(img));
      if (idx > -1) activeImageIndex = idx;
    }

    const formatMoney = (cents) => this.#formatMoney(cents);

    const calculateDiscount = (price, comparePrice) => {
      if (comparePrice > price && comparePrice > 0) {
        return Math.round(((comparePrice - price) * 100) / comparePrice);
      }
      return 0;
    };

    const buildGalleryHtml = () => {
      const currentSrc = images[activeImageIndex] || '';
      const hasMultiple = images.length > 1;

      const thumbnailsHtml = hasMultiple ? `
        <div class="btb-gallery__thumbnails">
          ${images.map((imgSrc, i) => `
            <button
              type="button"
              class="btb-gallery__thumb ${i === activeImageIndex ? 'btb-gallery__thumb--active' : ''}"
              data-gallery-thumb="${i}"
              aria-label="Bild ${i + 1}"
            >
              <img src="${imgSrc}" alt="" loading="lazy" />
            </button>
          `).join('')}
        </div>
      ` : '';

      return `
        <div class="btb-modal__gallery">
          <div class="btb-gallery__main">
            ${hasMultiple ? `
              <button type="button" class="btb-gallery__arrow btb-gallery__arrow--prev" data-gallery-action="prev" aria-label="Vorheriges Bild">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11L5 7L9 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            ` : ''}
            <img class="btb-gallery__image" src="${currentSrc}" alt="${(product.title || '').replace(/"/g, '&quot;')}" />
            ${hasMultiple ? `
              <button type="button" class="btb-gallery__arrow btb-gallery__arrow--next" data-gallery-action="next" aria-label="Nächstes Bild">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 11L9 7L5 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            ` : ''}
          </div>
          ${thumbnailsHtml}
        </div>
      `;
    };

    const buildDetailsHtml = () => {
      const price = currentVariant.price;
      let comparePrice = currentVariant.compare_at_price || 0;
      if (!comparePrice && bundleIndex) {
        comparePrice = this.#bundleEstimatedRetailPrices.get(bundleIndex)?.get(currentVariant.id) || 0;
      }
      const discount = calculateDiscount(price, comparePrice);

      let optionsHtml = '';
      if (product.options && product.options.length > 0 && product.variants.length > 1 && product.options[0].name !== 'Title') {
        optionsHtml = product.options.map((opt, optIndex) => {
          const optPos = opt.position || (optIndex + 1);
          const optName = typeof opt === 'string' ? opt : (opt.name || `Option ${optPos}`);
          const optValues = Array.isArray(opt.values) ? opt.values : [...new Set(product.variants.map((v) => v[`option${optPos}`]))];
          const optNameLower = optName.toLowerCase();
          const isColor = optNameLower.includes('color') || optNameLower.includes('farbe') || optNameLower.includes('config') || optPos === 1;
          const isSize = optNameLower.includes('size') || optNameLower.includes('größe') || optNameLower.includes('groesse') || optNameLower.includes('grösse');
          const currentOptVal = currentVariant[`option${optPos}`];

          if (isColor && !isSize) {
            // Render Swatches
            const swatchesHtml = optValues.map((val) => {
              const matchingVar = product.variants.find((v) => v[`option${optPos}`] === val && v.featured_image) || product.variants.find((v) => v[`option${optPos}`] === val);
              const swatchImg = normalizeImg(matchingVar?.featured_image) || images[0] || '';
              const isSelected = val === currentOptVal;

              return `
                <button
                  type="button"
                  class="btb-modal__swatch ${isSelected ? 'btb-modal__swatch--active' : ''}"
                  data-option-pos="${optPos}"
                  data-option-val="${String(val).replace(/"/g, '&quot;')}"
                  title="${String(val).replace(/"/g, '&quot;')}"
                >
                  ${swatchImg ? `<img src="${swatchImg}" alt="${String(val).replace(/"/g, '&quot;')}" />` : `<span>${val}</span>`}
                </button>
              `;
            }).join('');

            return `
              <div class="btb-modal__option-group" data-option-group="${optPos}">
                <div class="btb-modal__option-header">
                  <span class="btb-modal__option-label">${optName}: <span class="btb-modal__option-selected-val">${currentOptVal}</span></span>
                </div>
                <div class="btb-modal__swatches">
                  ${swatchesHtml}
                </div>
              </div>
            `;
          } else {
            // Render Size Pills
            const pillsHtml = optValues.map((val) => {
              const exists = product.variants.some((v) => {
                const matchOpt1 = !currentVariant.option1 || optPos === 1 || String(v.option1 || '').trim().toLowerCase() === String(currentVariant.option1).trim().toLowerCase();
                const matchOpt2 = !currentVariant.option2 || optPos === 2 || String(v.option2 || '').trim().toLowerCase() === String(currentVariant.option2).trim().toLowerCase();
                const matchOpt3 = !currentVariant.option3 || optPos === 3 || String(v.option3 || '').trim().toLowerCase() === String(currentVariant.option3).trim().toLowerCase();
                const matchCurrent = String(v[`option${optPos}`] || '').trim().toLowerCase() === String(val).trim().toLowerCase();
                return matchOpt1 && matchOpt2 && matchOpt3 && matchCurrent;
              });

              if (!exists) return '';

              const isSelected = val === currentOptVal;
              const isAvailable = product.variants.some((v) => {
                if (!v.available) return false;
                const matchOpt1 = !currentVariant.option1 || optPos === 1 || String(v.option1 || '').trim().toLowerCase() === String(currentVariant.option1).trim().toLowerCase();
                const matchOpt2 = !currentVariant.option2 || optPos === 2 || String(v.option2 || '').trim().toLowerCase() === String(currentVariant.option2).trim().toLowerCase();
                const matchOpt3 = !currentVariant.option3 || optPos === 3 || String(v.option3 || '').trim().toLowerCase() === String(currentVariant.option3).trim().toLowerCase();
                const matchCurrent = String(v[`option${optPos}`] || '').trim().toLowerCase() === String(val).trim().toLowerCase();
                return matchOpt1 && matchOpt2 && matchOpt3 && matchCurrent;
              });

              return `
                <button
                  type="button"
                  class="btb-modal__size-pill ${isSelected ? 'btb-modal__size-pill--active' : ''} ${!isAvailable ? 'btb-modal__size-pill--sold-out' : ''}"
                  data-option-pos="${optPos}"
                  data-option-val="${String(val).replace(/"/g, '&quot;')}"
                  ${!isAvailable ? 'disabled aria-disabled="true"' : ''}
                >
                  ${val}
                </button>
              `;
            }).join('');

            const rulerIconUrl = this.dataset.rulerIconUrl || document.querySelector('[data-ruler-icon-url]')?.dataset?.rulerIconUrl || '';
            const sizeGuideBtn = `
              <button
                type="button"
                class="variant-option__size-chart-link btb-modal__size-chart-link"
                data-size-chart-toggle
              >
                <span>Größentabelle</span>
                ${rulerIconUrl ? `<img class="variant-option__ruler-icon" src="${rulerIconUrl}" alt="" aria-hidden="true" width="26" height="22" loading="lazy">` : `
                  <svg class="variant-option__ruler-icon" width="26" height="22" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.512958 8.55589L8.55644 0.51241C8.8847 0.184153 9.32968 -0.000218921 9.79395 -0.000221355C10.258 -0.000221471 10.7032 0.184153 11.0313 0.512305L13.5064 2.98732C13.8345 3.31547 14.0189 3.76067 14.0189 4.22472C14.0189 4.68898 13.8345 5.13397 13.5063 5.46223L5.46278 13.5057C5.13462 13.8339 4.68953 14.0183 4.22527 14.0183C3.76122 14.0183 3.31602 13.834 2.98786 13.5058L0.51285 11.0308C0.184699 10.7027 0.000324565 10.2575 0.00032545 9.7934C0.000325134 9.32914 0.184806 8.88404 0.512958 8.55589ZM3.60667 12.887C3.77075 13.0509 3.99334 13.143 4.22526 13.1429C4.45729 13.1429 4.67979 13.0509 4.84406 12.887L12.8875 4.84353C13.0514 4.67925 13.1434 4.45675 13.1434 4.22473C13.1435 3.9928 13.0514 3.7702 12.8877 3.60603L10.4126 1.13102C10.2485 0.967251 10.0259 0.875172 9.79394 0.875278C9.56191 0.875278 9.33941 0.967268 9.17514 1.13112L10.1032 2.05923C10.2741 2.23003 10.2742 2.50712 10.1033 2.67793C9.93244 2.84884 9.65535 2.84874 9.48454 2.67793L8.55644 1.74982L7.93774 2.36852L10.1033 4.53414C10.2742 4.70494 10.2741 4.98203 10.1032 5.15284C9.93243 5.32364 9.65535 5.32375 9.48454 5.15294L7.31893 2.98733L6.70023 3.60603L7.62833 4.53414C7.79914 4.70494 7.79925 4.98203 7.62833 5.15294C7.45752 5.32375 7.18044 5.32364 7.00963 5.15284L6.08153 4.22473L5.46283 4.84343L7.62844 7.00904C7.79925 7.17985 7.79914 7.45694 7.62834 7.62774C7.45742 7.79866 7.18044 7.79865 7.00964 7.62785L4.84402 5.46224L4.22532 6.08094L5.15343 7.00904C5.32424 7.17985 5.32424 7.45704 5.15343 7.62785C4.98262 7.79866 4.70543 7.79866 4.53462 7.62785L3.60652 6.69974L2.98781 7.31845L5.15343 9.48406C5.32424 9.65487 5.32423 9.93185 5.15332 10.1028C4.98251 10.2736 4.70553 10.2736 4.53473 10.1028L2.36911 7.93715L1.75041 8.55585L2.67852 9.48395C2.84933 9.65476 2.84933 9.93195 2.67852 10.1028C2.50761 10.2737 2.23052 10.2736 2.05971 10.1028L1.13161 9.17465C0.96774 9.33893 0.875765 9.56143 0.875765 9.79345C0.875661 10.0254 0.967756 10.248 1.13161 10.412L3.60667 12.887Z" fill="currentColor"/>
                  </svg>
                `}
              </button>
            `;

            return `
              <div class="btb-modal__option-group" data-option-group="${optPos}">
                <div class="btb-modal__option-header">
                  <span class="btb-modal__option-label">${optName}: <span class="btb-modal__option-selected-val">${currentOptVal}</span></span>
                  ${isSize ? sizeGuideBtn : ''}
                </div>
                <div class="btb-modal__size-pills">
                  ${pillsHtml}
                </div>
              </div>
            `;
          }
        }).join('');
      }

      const isAvailable = currentVariant.available;
      const cachedAccHtml = accordionCache.get(currentVariant.id) || '';

      const rawRating = parseFloat(product.rating || product.average_rating || 0);
      const reviewsCount = parseInt(product.reviews_count || product.total_reviews || 0, 10);
      const cachedReviewsHtml = pdpReviewsCache.get(currentVariant.id);

      const renderStarsHtml = (rating) => {
        const ratingForStars = rating > 0 ? rating : 0;
        const fullStars = Math.floor(ratingForStars);
        const decimal = ratingForStars - fullStars;
        let effectiveRating = fullStars;
        if (decimal >= 0.8) {
          effectiveRating = fullStars + 1;
        } else if (decimal > 0.2) {
          effectiveRating = fullStars + 0.5;
        }

        let stars = '';
        for (let i = 1; i <= 5; i++) {
          const halfThreshold = i - 0.5;
          if (effectiveRating >= i) {
            stars += '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" fill="none" viewBox="0 0 16 15"><path fill="#f19300" fill-rule="evenodd" d="M7.892.2c.73 1.412 1.475 2.817 2.212 4.226l4.703.798a.376.376 0 0 1 .206.633l-3.335 3.41.694 4.72a.375.375 0 0 1-.555.382L7.56 12.26l-4.274 2.118a.374.374 0 0 1-.537-.39l.694-4.72-3.336-3.41a.377.377 0 0 1 .229-.636l4.68-.795L7.228.202a.375.375 0 0 1 .664 0z" clip-rule="evenodd"/></svg>';
          } else if (effectiveRating >= halfThreshold) {
            stars += '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" fill="none" viewBox="0 0 16 15"><path fill="#f19300" fill-rule="evenodd" d="M7.893.2c.73 1.41 1.474 2.814 2.212 4.22l4.702.798a.375.375 0 0 1 .207.633l-3.335 3.406.694 4.714a.375.375 0 0 1-.555.382L7.56 12.244l-4.275 2.117a.375.375 0 0 1-.537-.39l.694-4.714L.108 5.852a.376.376 0 0 1 .228-.637l4.68-.794L7.23.201a.375.375 0 0 1 .664 0" clip-rule="evenodd"/><mask id="btb_star_half" width="16" height="15" x="0" y="0" maskUnits="userSpaceOnUse" style="mask-type:alpha"><path fill="#eee" fill-rule="evenodd" d="M7.892.2c.73 1.41 1.475 2.814 2.212 4.22l4.703.798a.375.375 0 0 1 .206.633l-3.335 3.406.694 4.714a.375.375 0 0 1-.555.382L7.56 12.244l-4.274 2.117a.375.375 0 0 1-.502-.17c-.035-.071-.045-.148-.035-.22l.694-4.714L.107 5.851a.376.376 0 0 1 .229-.636l4.68-.794L7.228.201a.375.375 0 0 1 .664 0" clip-rule="evenodd"/></mask><g mask="url(#btb_star_half)"><path fill="#ddd" d="M7.56 0h7.56v14.4H7.56z"/></g></svg>';
          } else {
            stars += '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" fill="none" viewBox="0 0 15 14"><path fill="#ddd" d="M6.68.429c.186-.572.996-.572 1.181 0l1.291 3.973a.62.62 0 0 0 .59.429h4.178c.601 0 .851.77.365 1.123l-3.38 2.455a.62.62 0 0 0-.225.694l1.29 3.973c.186.572-.468 1.047-.955.694l-3.38-2.455a.62.62 0 0 0-.729 0l-3.38 2.455c-.486.353-1.14-.122-.955-.694l1.291-3.973a.62.62 0 0 0-.225-.694L.257 5.954C-.229 5.6.021 4.83.622 4.83H4.8a.62.62 0 0 0 .59-.43z"/></svg>';
          }
        }
        return stars;
      };

      let reviewsHtmlSnippet = '';
      if (cachedReviewsHtml) {
        reviewsHtmlSnippet = cachedReviewsHtml;
      } else if (rawRating > 0 && reviewsCount > 0) {
        reviewsHtmlSnippet = `
          <div
            class="desktop-sticky-atc__reviews btb-modal__reviews"
            role="img"
            aria-label="${rawRating} von 5 Sternen, ${reviewsCount} Bewertungen"
          >
            <span class="desktop-sticky-atc__review-stars btb-modal__review-stars" aria-hidden="true">
              ${renderStarsHtml(rawRating)}
            </span>
            <span class="desktop-sticky-atc__review-count btb-modal__review-count" aria-hidden="true">(${reviewsCount})</span>
          </div>
        `;
      } else if (rawRating > 0 || reviewsCount > 0) {
        const displayCount = reviewsCount > 0 ? reviewsCount : rawRating.toFixed(1);
        reviewsHtmlSnippet = `
          <div
            class="desktop-sticky-atc__reviews btb-modal__reviews"
            role="img"
            aria-label="${rawRating || displayCount} von 5 Sternen, ${displayCount} Bewertungen"
          >
            <span class="desktop-sticky-atc__review-stars btb-modal__review-stars" aria-hidden="true">
              ${renderStarsHtml(rawRating)}
            </span>
            <span class="desktop-sticky-atc__review-count btb-modal__review-count" aria-hidden="true">(${displayCount})</span>
          </div>
        `;
      }

      const pdpLiveTitle = pdpTitleCache.get(currentVariant.id);
      const resolvedTitle = pdpLiveTitle
        || currentVariant.variant_title
        || currentVariant.variantTitle
        || (currentVariant.name && currentVariant.name !== product.title && !currentVariant.name.includes(' - ') ? currentVariant.name : null)
        || product.title;

      return `
        <h3 class="btb-modal__title">${resolvedTitle}</h3>
          ${reviewsHtmlSnippet}
        <div class="btb-modal__prices">
          <span class="btb-modal__current-price">${formatMoney(price)}</span>
          ${comparePrice > price ? `<span class="btb-modal__compare-price">${formatMoney(comparePrice)}</span>` : ''}
          ${discount > 0 ? `<span class="btb-modal__discount-badge">${discount}% RABATT</span>` : ''}
        </div>
        <hr class="btb-modal__divider" />
        ${optionsHtml}
        <div class="btb-modal__accordion-wrapper">${cachedAccHtml}</div>
      `;
    };

    const attachAccordionEvents = (wrapper) => {
      if (!wrapper) return;

      const items = wrapper.querySelectorAll('[data-acc-item]');
      if (!items.length) return;

      items.forEach((item, index) => {
        const toggle = item.querySelector('[data-acc-toggle]');
        const panel = item.querySelector('[data-acc-panel]');
        if (!toggle || !panel) return;

        // Auto open Produktinformationen (first accordion item) on first load
        if (index === 0 || item.id === 'pdp-accordion-productinfo') {
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }

        // Clear any prior conflicting inline styles
        panel.style.removeProperty('height');
        panel.style.removeProperty('display');
        panel.style.removeProperty('overflow');

        if (item.dataset.btbBound === 'true') return;
        item.dataset.btbBound = 'true';

        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isOpen = item.classList.contains('is-open');
          const readMoreBtn = item.querySelector('[data-readmore]');
          if (isOpen) {
            item.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            if (readMoreBtn) readMoreBtn.style.display = 'none';
          } else {
            item.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            if (readMoreBtn && !readMoreBtn.hidden && readMoreBtn.dataset.readmoreNeeded === 'true') {
              readMoreBtn.style.display = 'flex';
            }
            setTimeout(() => {
              const grid = container.querySelector('.btb-modal__grid');
              if (grid) {
                const itemRect = item.getBoundingClientRect();
                const gridRect = grid.getBoundingClientRect();
                if (itemRect.bottom > gridRect.bottom - 20) {
                  item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }
            }, 60);
          }
        });

        const readMoreBtn = item.querySelector('[data-readmore]');
        const readMoreContent = item.querySelector('[data-readmore-content]');
        if (readMoreBtn && readMoreContent) {
          const textNode = readMoreBtn.querySelector('.pdp-accordion__readmore-text');
          const showMoreText = readMoreBtn.dataset.showMore || 'Mehr anzeigen';
          const showLessText = readMoreBtn.dataset.showLess || 'Weniger anzeigen';
          const collapsedMaxHeight = 120; // Collapsed height limit in modal

          // Check natural content height
          readMoreContent.style.height = 'auto';
          readMoreContent.style.maxHeight = 'none';
          const fullScrollHeight = readMoreContent.scrollHeight;

          if (fullScrollHeight <= collapsedMaxHeight + 30) {
            readMoreBtn.dataset.readmoreNeeded = 'false';
            readMoreBtn.style.display = 'none';
            readMoreContent.style.maxHeight = 'none';
            readMoreContent.classList.remove('is-collapsed', 'is-expanded');
          } else {
            readMoreBtn.dataset.readmoreNeeded = 'true';
            readMoreBtn.style.display = item.classList.contains('is-open') ? 'flex' : 'none';
            if (!readMoreContent.classList.contains('is-expanded')) {
              readMoreContent.classList.add('is-collapsed');
              readMoreContent.classList.remove('is-expanded');
              readMoreContent.style.maxHeight = `${collapsedMaxHeight}px`;
              readMoreContent.style.overflow = 'hidden';
              readMoreBtn.classList.remove('is-open');
              readMoreBtn.setAttribute('aria-expanded', 'false');
              if (textNode) textNode.textContent = showMoreText;
            } else {
              readMoreContent.style.maxHeight = 'none';
              readMoreBtn.classList.add('is-open');
              readMoreBtn.setAttribute('aria-expanded', 'true');
              if (textNode) textNode.textContent = showLessText;
            }
          }

          if (readMoreBtn.dataset.btbReadmoreBound !== 'true') {
            readMoreBtn.dataset.btbReadmoreBound = 'true';
            readMoreBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const isExpanded = readMoreContent.classList.contains('is-expanded');
              if (isExpanded) {
                readMoreContent.classList.remove('is-expanded');
                readMoreContent.classList.add('is-collapsed');
                readMoreContent.style.maxHeight = `${collapsedMaxHeight}px`;
                readMoreContent.style.overflow = 'hidden';
                readMoreBtn.classList.remove('is-open');
                readMoreBtn.setAttribute('aria-expanded', 'false');
                if (textNode) textNode.textContent = showMoreText;
              } else {
                readMoreContent.classList.remove('is-collapsed');
                readMoreContent.classList.add('is-expanded');
                readMoreContent.style.maxHeight = 'none';
                readMoreContent.style.overflow = 'visible';
                readMoreBtn.classList.add('is-open');
                readMoreBtn.setAttribute('aria-expanded', 'true');
                if (textNode) textNode.textContent = showLessText;
                setTimeout(() => {
                  const grid = container.querySelector('.btb-modal__grid');
                  if (grid) {
                    const itemRect = item.getBoundingClientRect();
                    const gridRect = grid.getBoundingClientRect();
                    if (itemRect.bottom > gridRect.bottom - 20) {
                      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }
                }, 60);
              }
            });
          }
        }
      });
    };

    const render = () => {
      // Save scroll positions before replacing innerHTML to prevent scrolling back to top on mobile/desktop
      const prevGrid = container.querySelector('.btb-modal__grid');
      const prevDetailsScroll = container.querySelector('.btb-modal__details-scroll');
      const prevThumbs = container.querySelector('.btb-gallery__thumbnails');

      const savedGridScrollTop = prevGrid ? prevGrid.scrollTop : 0;
      const savedDetailsScrollTop = prevDetailsScroll ? prevDetailsScroll.scrollTop : 0;
      const savedThumbsScrollLeft = prevThumbs ? prevThumbs.scrollLeft : 0;
      const savedContainerScrollTop = container.scrollTop || 0;

      const atcHtml = `
        <div class="btb-modal__atc-sticky-wrapper">
          <button
            type="button"
            class="btb-modal__atc"
            data-modal-atc
            ${!currentVariant.available ? 'disabled' : ''}
          >
            <span class="btb-modal__atc-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 3 25 25">
                <path stroke="currentColor" stroke-width="1.2" d="M10.5 25.7a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm9 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/>
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M2 5.2h2.657l4.655 14h10.622"/>
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M8.276 16.018 5.72 8.382h16.615c.105 0 .209.024.302.07a.66.66 0 0 1 .237.194.62.62 0 0 1 .091.573l-2.214 6.364a.64.64 0 0 1-.242.315.7.7 0 0 1-.388.12z"/>
              </svg>
            </span>
            <span class="btb-modal__atc-text">${currentVariant.available ? 'In den Warenkorb' : 'Ausverkauft'}</span>
          </button>
        </div>
      `;

      container.innerHTML = `
        <div class="btb-modal__content-wrapper">
          <div class="btb-modal__grid">
            ${buildGalleryHtml()}
            <div class="btb-modal__details">
              <div class="btb-modal__details-scroll">
                ${buildDetailsHtml()}
              </div>
              <div class="btb-modal__atc-desktop">
                ${atcHtml}
              </div>
            </div>
          </div>
          <div class="btb-modal__atc-mobile">
            ${atcHtml}
          </div>
        </div>
      `;

      // Restore scroll positions immediately after DOM replacement
      const newGrid = container.querySelector('.btb-modal__grid');
      if (newGrid && savedGridScrollTop) {
        newGrid.scrollTop = savedGridScrollTop;
      }
      const newDetailsScroll = container.querySelector('.btb-modal__details-scroll');
      if (newDetailsScroll && savedDetailsScrollTop) {
        newDetailsScroll.scrollTop = savedDetailsScrollTop;
      }
      const newThumbs = container.querySelector('.btb-gallery__thumbnails');
      if (newThumbs && savedThumbsScrollLeft) {
        newThumbs.scrollLeft = savedThumbsScrollLeft;
      }
      if (savedContainerScrollTop) {
        container.scrollTop = savedContainerScrollTop;
      }

      const accWrapper = container.querySelector('.btb-modal__accordion-wrapper');
      const vId = currentVariant.id;

      if (accordionCache.has(vId)) {
        if (accWrapper) {
          accWrapper.innerHTML = accordionCache.get(vId) || '';
          attachAccordionEvents(accWrapper);
        }
      } else if (typeof fetchVariantAccordion === 'function') {
          fetchVariantAccordion(vId).then(({ html, title: fetchedTitle, reviewsHtml: fetchedReviewsHtml }) => {
          if (currentVariant.id === vId) {
            if (accWrapper && html) {
              accWrapper.innerHTML = html;
              attachAccordionEvents(accWrapper);
            }
            if (fetchedTitle) {
              const titleNode = container.querySelector('.btb-modal__title');
              if (titleNode && titleNode.textContent !== fetchedTitle) {
                titleNode.textContent = fetchedTitle;
              }
            }
            if (fetchedReviewsHtml) {
              const reviewNode = container.querySelector('.desktop-sticky-atc__reviews, .btb-modal__reviews');
              if (reviewNode) {
                reviewNode.outerHTML = fetchedReviewsHtml;
              } else {
                const titleNode = container.querySelector('.btb-modal__title');
                if (titleNode) {
                  titleNode.insertAdjacentHTML('afterend', fetchedReviewsHtml);
                }
              }
            }
          }
        });
      }

      // Gallery Controls
      const prevBtn = container.querySelector('[data-gallery-action="prev"]');
      const nextBtn = container.querySelector('[data-gallery-action="next"]');
      const thumbs = container.querySelectorAll('[data-gallery-thumb]');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          activeImageIndex = (activeImageIndex - 1 + images.length) % images.length;
          render();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          activeImageIndex = (activeImageIndex + 1) % images.length;
          render();
        });
      }

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const idx = parseInt(thumb.getAttribute('data-gallery-thumb') || '0', 10);
          activeImageIndex = idx;
          render();
        });
      });

      // Swatch & Size Pill Selection
      container.querySelectorAll('[data-option-pos]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const pos = parseInt(btn.getAttribute('data-option-pos') || '1', 10);
          const val = btn.getAttribute('data-option-val') || '';

          const selectedOpts = {
            option1: currentVariant.option1,
            option2: currentVariant.option2,
            option3: currentVariant.option3,
            [`option${pos}`]: val,
          };

          const matched = product.variants.find((v) => {
            return (
              (!selectedOpts.option1 || v.option1 === selectedOpts.option1) &&
              (!selectedOpts.option2 || v.option2 === selectedOpts.option2) &&
              (!selectedOpts.option3 || v.option3 === selectedOpts.option3)
            );
          }) || product.variants.find((v) => v[`option${pos}`] === val);

          if (matched) {
            currentVariant = matched;
            const matchedImg = normalizeImg(matched.featured_image);
            if (matchedImg) {
              const imgIdx = images.findIndex((img) => img.includes(matchedImg) || matchedImg.includes(img));
              if (imgIdx > -1) activeImageIndex = imgIdx;
            }
            render();
          }
        });
      });

      // Add to Cart in modal
      const atcBtns = container.querySelectorAll('[data-modal-atc]');
      atcBtns.forEach((atcBtn) => {
        atcBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          if (!currentVariant.available) return;

          atcBtns.forEach((btn) => btn.setAttribute('disabled', ''));
          container.querySelectorAll('.btb-modal__atc-text').forEach((el) => {
            el.textContent = 'Wird hinzugefügt...';
          });

          try {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: Number(currentVariant.id), quantity: 1 }),
            });

            if (!addRes.ok) throw new Error('Add to cart failed');
            const addedItem = await addRes.json();

            const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            const cart = await cartRes.json();

            document.querySelectorAll('[data-cart-count], [data-cart-icon-count], .cart-count, .header__cart-count').forEach((el) => {
              el.textContent = String(cart.item_count || 0);
              el.classList.remove('hidden', 'is-hidden');
              el.removeAttribute('hidden');
            });

            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { addedItem, cart } }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            document.dispatchEvent(new CustomEvent('cart:open'));

            this.#closeProductModal();
          } catch (err) {
            console.error('[BuyTheBundle] Modal add to cart error:', err);
            atcBtns.forEach((btn) => btn.removeAttribute('disabled'));
            container.querySelectorAll('.btb-modal__atc-text').forEach((el) => {
              el.textContent = 'In den Warenkorb';
            });
          }
        });
      });
    };

    render();
  }

  // --- Helpers ---

  /**
   * @param {number} idx
   * @returns {NodeListOf<Element>}
   */
  #getOptionInputs(idx) {
    return this.querySelectorAll(`.buy-the-bundle__custom-dropdown-input[data-bundle-index="${idx}"]`);
  }

  /**
   * @param {NodeListOf<Element>} inputs
   * @returns {Record<string, string>}
   */
  #readSelectedOptions(inputs) {
    /** @type {Record<string, string>} */
    const opts = {};
    for (const el of inputs) {
      const inp = /** @type {HTMLInputElement} */ (el);
      if (inp.dataset.optionPosition) opts[`option${inp.dataset.optionPosition}`] = inp.value;
    }
    return opts;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;

    return `€${amount.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}

if (!customElements.get('buy-the-bundle-component')) {
  customElements.define('buy-the-bundle-component', BuyTheBundleComponent);
}
